import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  BarChart3,
  Bold,
  Command as CommandIcon,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Image,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link2,
  List,
  ListOrdered,
  MoreHorizontal,
  Map as MapIcon,
  Loader2,
  Pilcrow,
  Quote,
  Redo2,
  RotateCcw,
  Search,
  Strikethrough,
  Table2,
  Underline,
  Undo2,
} from 'lucide-react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createRangeSelection,
  $createParagraphNode,
  $createTextNode,
  COMMAND_PRIORITY_HIGH,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_DOWN_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $setSelection,
} from 'lexical';
import {
  HeadingNode,
  $isHeadingNode,
  $createQuoteNode,
  $isQuoteNode,
  QuoteNode,
} from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import {
  $createListItemNode,
  $createListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
  $isListNode,
} from '@lexical/list';
import { LinkNode, TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  INSERT_TABLE_COMMAND,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import {
  HeadingAnchorNode,
  $createHeadingAnchorNode,
  $isHeadingAnchorNode,
} from './nodes/HeadingAnchorNode';
import { TableOfContentsNode, $createTableOfContentsNode } from './nodes/TableOfContentsNode';
import { PlaceholderNode, $createPlaceholderNode } from './nodes/PlaceholderNode';
import { ReportVisualNode, $createReportVisualNode } from './nodes/ReportVisualNode';
import { ReportCoverNode, $createReportCoverNode } from './nodes/ReportCoverNode';
import { ReportPagedCanvas } from './pagination/ReportPagedCanvas';
import { AIIsland } from './AIIsland/AIIsland';
import { ReportDraftVisualNodeProvider } from './ReportDraftVisualNodeContext';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { ReportDraftStyleMenu } from './toolbar/ReportDraftStyleMenu';
import { ReportDraftInsertMenu } from './toolbar/ReportDraftInsertMenu';
import { ReportDraftCommandPalette } from './toolbar/ReportDraftCommandPalette';
import { ReportDraftFindReplacePanel } from './toolbar/ReportDraftFindReplacePanel';
import { SlashCommandPlugin } from './plugins/SlashCommandPlugin';
import { FindReplacePlugin } from './plugins/FindReplacePlugin';
import { ReportDraftVisualDropInsertPlugin } from './plugins/ReportDraftVisualDropInsertPlugin';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { ReportDraftVisualPickerDialog } from './toolbar/ReportDraftVisualPickerDialog';
import {
  buildAnalyticsVisualPresetItems,
  buildBlankVisualNodeData,
  buildCurrentCoverageMapVisualItem,
  collectTemplateVisualInsertItems,
} from './visuals/reportDraftVisualInsertCatalog';
import { getSerializedEditorState } from '../../../utils/reportDraftStorage';
import {
  createDraftClientInstanceId,
  isReportDraftLocalCacheEnabled,
  markSyncError,
  markSyncSuccess,
  normalizeDraftPayload,
  replaceLocalWithServerDraft,
  saveLocalDraftSnapshot,
  syncDraftToServer,
} from '../../../utils/reportDraftRepository';
import { BRAND } from '../../../config/brand';
import './ReportDraftEditor.css';

const LOCAL_AUTOSAVE_DEBOUNCE_MS = 200;
const REMOTE_SYNC_DEBOUNCE_MS = 500;
const REMOTE_SYNC_RETRY_DELAYS_MS = [2000, 5000, 15000, 60000];
const ACTIVITY_LOG_THROTTLE_MS = 3 * 60 * 1000;
const AUTOSAVE_SAVE_TIMEOUT_MS = 8000;
const AUTOSAVE_SLOW_STATUS_MS = 2000;
const PLACEHOLDER_TOKEN_REGEX = /\[([A-Z]+):\s*([^\]]+)\]/g;
const PLACEHOLDER_ONLY_REGEX = /^\[([A-Z]+):\s*([^\]]+)\]$/;
const KNOWN_PLACEHOLDER_TYPES = new Set([
  'MANUAL',
  'OPTIONAL',
  'CAPTION',
  'TABLE',
  'MAP',
  'IMAGE',
  'CHART',
]);
const INLINE_PLACEHOLDER_STYLE = 'color: var(--color-gray-500); font-style: italic;';
const META_BLOCK_STYLE = [
  'background: var(--color-gray-50)',
  'border: 1px solid var(--color-gray-100)',
  'border-left: 3px solid var(--color-gray-200)',
  'border-radius: var(--radius-md)',
  'padding: 8px 12px',
  'font-size: 14px',
  'color: var(--color-gray-600)',
].join(';');

const TEXT_STYLE_ACTIONS = ['paragraph', 'h1', 'h2', 'h3', 'quote', 'bullet', 'number'];
const LIST_INSERT_ACTIONS = ['bullets', 'numbered'];
const TOOLBAR_POPOVER_Z_INDEX = 1400;
const ADVANCED_REPORT_EDITOR_ENABLED = process.env.REACT_APP_REPORT_EDITOR_ADVANCED !== 'false';
const REPORT_DRAFT_LOCAL_CACHE_ENABLED = isReportDraftLocalCacheEnabled();

const createAutosaveTimeoutError = () => {
  const error = new Error('Autosave is taking too long. Please retry.');
  error.code = 'save_timeout';
  return error;
};

const isStandalonePlaceholder = (text) => {
  if (text == null) {
    return false;
  }
  const match = String(text).trim().match(PLACEHOLDER_ONLY_REGEX);
  return Boolean(match && KNOWN_PLACEHOLDER_TYPES.has(match[1]));
};

const reportDraftTheme = {
  paragraph: 'report-draft-editor__paragraph',
  heading: {
    h1: 'report-draft-editor__heading report-draft-editor__heading--h1',
    h2: 'report-draft-editor__heading report-draft-editor__heading--h2',
    h3: 'report-draft-editor__heading report-draft-editor__heading--h3',
  },
  list: {
    ul: 'report-draft-editor__list report-draft-editor__list--ul',
    ol: 'report-draft-editor__list report-draft-editor__list--ol',
    listitem: 'report-draft-editor__list-item',
  },
  table: 'report-draft-editor__table',
  tableCell: 'report-draft-editor__table-cell',
  tableCellHeader: 'report-draft-editor__table-cell report-draft-editor__table-cell--header',
  link: 'report-draft-editor__link',
  text: {
    bold: 'report-draft-editor__text--bold',
    italic: 'report-draft-editor__text--italic',
    underline: 'report-draft-editor__text--underline',
    strikethrough: 'report-draft-editor__text--strikethrough',
    code: 'report-draft-editor__text--code',
    highlight: 'report-draft-editor__text--highlight',
  },
  quote: 'report-draft-editor__quote',
};

const reportDraftNodes = [
  ReportCoverNode,
  ReportVisualNode,
  HeadingAnchorNode,
  TableOfContentsNode,
  PlaceholderNode,
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  LinkNode,
  TableNode,
  TableRowNode,
  TableCellNode,
];

const getHeadingTag = (level) => {
  if (level === 1) return 'h1';
  if (level === 2) return 'h2';
  return 'h3';
};

const getHeadingLevel = (tag) => {
  const level = Number(String(tag || '').toLowerCase().replace('h', ''));
  if (!Number.isFinite(level)) {
    return 1;
  }
  return Math.min(Math.max(level, 1), 3);
};

const createAnchorId = (baseId, usedIds) => {
  const normalized = String(baseId || 'section')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
  let anchorId = normalized || 'section';
  let index = 1;
  while (usedIds.has(anchorId)) {
    anchorId = `${normalized || 'section'}-${index}`;
    index += 1;
  }
  usedIds.add(anchorId);
  return anchorId;
};

const resolvePagedHeaderLabel = (templateDocument, fallbackLabel) => {
  const coverBlock = (templateDocument?.sections || [])
    .flatMap(section => section?.blocks || [])
    .find(block => block?.type === 'cover');
  const titleCandidate = String(
    coverBlock?.title
    || coverBlock?.lines?.[0]
    || fallbackLabel
    || ''
  ).trim();
  return titleCandidate || BRAND.reportTitle;
};

const createPlaceholderBlock = (text) => {
  const node = $createPlaceholderNode();
  appendTextWithPlaceholders(node, text);
  return node;
};

const createParagraph = (text, style) => {
  const paragraph = $createParagraphNode();
  if (style) {
    paragraph.setStyle(style);
  }
  if (text != null && text !== '') {
    appendTextWithPlaceholders(paragraph, text);
  }
  return paragraph;
};

const createMetaParagraph = (label, value) => {
  const paragraph = $createParagraphNode();
  paragraph.setStyle(META_BLOCK_STYLE);
  const hasValue = value != null && value !== '';
  if (label) {
    const labelNode = $createTextNode(`${label}${hasValue ? ': ' : ''}`);
    labelNode.setStyle('font-weight: 600; color: var(--color-gray-700);');
    paragraph.append(labelNode);
  }
  if (hasValue) {
    appendTextWithPlaceholders(paragraph, value);
  }
  return paragraph;
};

function appendTextWithPlaceholders(parentNode, rawText, placeholderStyle = INLINE_PLACEHOLDER_STYLE) {
  if (rawText == null) {
    return;
  }
  const text = String(rawText);
  if (!text) {
    return;
  }
  PLACEHOLDER_TOKEN_REGEX.lastIndex = 0;
  let lastIndex = 0;
  let match = PLACEHOLDER_TOKEN_REGEX.exec(text);
  while (match) {
    const [full, type, label] = match;
    if (KNOWN_PLACEHOLDER_TYPES.has(type)) {
      const start = match.index;
      if (start > lastIndex) {
        parentNode.append($createTextNode(text.slice(lastIndex, start)));
      }
      const placeholderNode = $createTextNode(label.trim());
      if (placeholderStyle) {
        placeholderNode.setStyle(placeholderStyle);
      }
      parentNode.append(placeholderNode);
      lastIndex = start + full.length;
    }
    match = PLACEHOLDER_TOKEN_REGEX.exec(text);
  }
  if (lastIndex === 0) {
    parentNode.append($createTextNode(text));
    return;
  }
  if (lastIndex < text.length) {
    parentNode.append($createTextNode(text.slice(lastIndex)));
  }
}

const VISUAL_PLACEHOLDER_REGEX = /^(map|chart|image):\s*(.*)$/i;

const collectTemplateVisualBlocks = (templateDocument) => {
  const collected = {
    cover: null,
    map: [],
    chart: [],
    image: [],
  };

  const visitSection = (section) => {
    (section?.blocks || []).forEach((block) => {
      if (!block || typeof block !== 'object') {
        return;
      }
      if (block.type === 'cover' && !collected.cover) {
        collected.cover = block;
        return;
      }
      if (block.type === 'map' || block.type === 'chart' || block.type === 'image') {
        collected[block.type].push(block);
      }
    });
    (section?.children || []).forEach(visitSection);
  };

  (templateDocument?.sections || []).forEach(visitSection);
  return collected;
};

const getSerializedNodeText = (node) => {
  if (!node || !Array.isArray(node.children)) {
    return '';
  }
  return node.children
    .map((child) => (typeof child?.text === 'string' ? child.text : ''))
    .join('')
    .trim();
};

const normalizeVisualType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'map' || normalized === 'chart' || normalized === 'image') {
    return normalized;
  }
  return null;
};

const toSerializedVisualNode = (block, visualType) => ({
  type: 'report-visual',
  version: 1,
  data: {
    ...(block || {}),
    visualType,
  },
});

const toSerializedCoverNode = (block = {}) => ({
  type: 'report-cover',
  version: 1,
  data: {
    ...block,
    title: block.title || block.lines?.[0] || '',
    subtitle: block.subtitle || block.lines?.[1] || '',
    referenceLine: block.referenceLine || block.lines?.[2] || '',
    metadataRows: block.essentialRows || block.metadataRows || [],
    essentialRows: block.essentialRows || block.metadataRows || [],
    extendedRows: block.extendedRows || [],
    metadataSectionTitle: block.metadataSectionTitle || '',
  },
});

const templateHasToc = (templateDocument) => (
  Array.isArray(templateDocument?.sections)
  && templateDocument.sections.some((section) => (
    Array.isArray(section?.blocks)
    && section.blocks.some(block => block?.type === 'toc')
  ))
);

const canonicalizeStructuralNodes = ({
  children,
  templateCoverBlock,
  includeToc,
}) => {
  const sourceChildren = Array.isArray(children) ? children : [];
  const coverNodes = [];
  const tocNodes = [];
  const otherNodes = [];

  sourceChildren.forEach((node) => {
    if (node?.type === 'report-cover') {
      coverNodes.push(node);
      return;
    }
    if (node?.type === 'table-of-contents') {
      tocNodes.push(node);
      return;
    }
    otherNodes.push(node);
  });

  let changed = coverNodes.length > 1 || tocNodes.length > 1;
  let primaryCover = coverNodes[0] || null;
  let primaryToc = tocNodes[0] || null;

  if (!primaryCover && templateCoverBlock) {
    primaryCover = toSerializedCoverNode(templateCoverBlock);
    changed = true;
  }

  if (!primaryToc && includeToc) {
    primaryToc = {
      type: 'table-of-contents',
      version: 1,
    };
    changed = true;
  }

  const normalizedChildren = [
    ...(primaryCover ? [primaryCover] : []),
    ...(primaryToc ? [primaryToc] : []),
    ...otherNodes,
  ];

  if (!changed) {
    changed = normalizedChildren.length !== sourceChildren.length
      || normalizedChildren.some((node, index) => node !== sourceChildren[index]);
  }

  return {
    children: normalizedChildren,
    changed,
  };
};

export const migrateLegacyVisualPlaceholders = (serializedState, templateDocument) => {
  if (!serializedState || typeof serializedState !== 'string') {
    return serializedState;
  }

  let parsed;
  try {
    parsed = JSON.parse(serializedState);
  } catch (error) {
    return serializedState;
  }

  if (!parsed?.root || !Array.isArray(parsed.root.children)) {
    return serializedState;
  }

  const templateVisuals = collectTemplateVisualBlocks(templateDocument);
  const includeToc = templateHasToc(templateDocument);
  const visualPools = {
    map: (templateVisuals.map || []).map((block) => ({ ...block, __used: false })),
    chart: (templateVisuals.chart || []).map((block) => ({ ...block, __used: false })),
    image: (templateVisuals.image || []).map((block) => ({ ...block, __used: false })),
  };

  let changed = false;
  const migratedChildren = parsed.root.children.map((childNode) => {
    if (!childNode || (childNode.type !== 'paragraph' && childNode.type !== 'placeholder')) {
      return childNode;
    }
    const nodeText = getSerializedNodeText(childNode);
    const placeholderMatch = nodeText.match(VISUAL_PLACEHOLDER_REGEX);
    if (!placeholderMatch) {
      return childNode;
    }

    const visualType = normalizeVisualType(placeholderMatch[1]);
    if (!visualType) {
      return childNode;
    }

    const visualTitle = String(placeholderMatch[2] || '').trim().toLowerCase();
    const pool = visualPools[visualType] || [];
    let selected = null;

    if (visualTitle) {
      selected = pool.find((block) => !block.__used && String(block.title || '').trim().toLowerCase() === visualTitle)
        || pool.find((block) => !block.__used && String(block.title || '').toLowerCase().includes(visualTitle));
    }

    if (!selected) {
      selected = pool.find((block) => !block.__used);
    }

    if (!selected) {
      return childNode;
    }

    selected.__used = true;
    changed = true;
    return toSerializedVisualNode(selected, visualType);
  });

  const canonicalized = canonicalizeStructuralNodes({
    children: migratedChildren,
    templateCoverBlock: templateVisuals.cover,
    includeToc,
  });
  changed = changed || canonicalized.changed;

  if (!changed) {
    return serializedState;
  }

  parsed.root.children = canonicalized.children;
  try {
    return JSON.stringify(parsed);
  } catch (error) {
    return serializedState;
  }
};

export const buildEditorStateFromTemplate = (templateDocument, placeholderLabel) => {
  const root = $getRoot();
  root.clear();

  if (!templateDocument || !Array.isArray(templateDocument.sections)) {
    root.append(createParagraph(''));
    return;
  }

  const usedAnchors = new Set();
  const appendLabeledLine = (label, value) => {
    if (label == null && (value == null || value === '')) {
      return;
    }
    const resolvedValue = value ?? '';
    const line = label ? `${label}: ${resolvedValue}` : String(resolvedValue);
    if (!line) {
      return;
    }
    const shouldUsePlaceholder = isStandalonePlaceholder(resolvedValue);
    root.append(
      shouldUsePlaceholder ? createPlaceholderBlock(line) : createParagraph(line)
    );
  };

  const appendBlocks = (blocks = []) => {
    blocks.forEach((block) => {
      switch (block.type) {
        case 'cover': {
          const coverRows = block.essentialRows || block.metadataRows || [];
          root.append($createReportCoverNode({
            ...block,
            title: block.title || block.lines?.[0] || '',
            subtitle: block.subtitle || block.lines?.[1] || '',
            referenceLine: block.referenceLine || block.lines?.[2] || '',
            metadataRows: coverRows,
            essentialRows: coverRows,
            extendedRows: block.extendedRows || [],
            metadataSectionTitle: block.metadataSectionTitle || '',
          }));
          break;
        }
        case 'paragraph':
          if (isStandalonePlaceholder(block.text)) {
            root.append(createPlaceholderBlock(block.text));
          } else {
            root.append(createParagraph(block.text || ''));
          }
          break;
        case 'meta':
          root.append(createMetaParagraph(block.label ?? '', block.value ?? ''));
          break;
        case 'fieldList': {
          const list = $createListNode('bullet');
          (block.items || []).forEach((item) => {
            const listItem = $createListItemNode();
            if (item.label) {
              const hasValue = item.value != null && item.value !== '';
              const labelNode = $createTextNode(`${item.label}${hasValue ? ': ' : ''}`);
              labelNode.setStyle('font-weight: 600; color: var(--color-gray-900);');
              listItem.append(labelNode);
            }
            if (item.value != null && item.value !== '') {
              appendTextWithPlaceholders(listItem, item.value);
            }
            list.append(listItem);
          });
          root.append(list);
          break;
        }
        case 'gapReport': {
          if (!block.items || block.items.length === 0) {
            root.append(createParagraph(block.emptyMessage || ''));
            break;
          }
          const list = $createListNode('bullet');
          block.items.forEach((item) => {
            const listItem = $createListItemNode();
            listItem.append($createTextNode(item.label || ''));
            list.append(listItem);
          });
          root.append(list);
          break;
        }
        case 'table': {
          const tableNode = $createTableNode();
          const columns = block.columns || [];
          const rows = block.rows || [];
          if (columns.length > 0) {
            const headerRow = $createTableRowNode();
            columns.forEach((col) => {
              const cell = $createTableCellNode(TableCellHeaderStates.ROW);
              const paragraph = $createParagraphNode();
              paragraph.append($createTextNode(col.label || ''));
              cell.append(paragraph);
              headerRow.append(cell);
            });
            tableNode.append(headerRow);
          }
          rows.forEach((row) => {
            const rowNode = $createTableRowNode();
            columns.forEach((col) => {
              const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
              const paragraph = $createParagraphNode();
              const value = row[col.key];
              const cellValue = value != null && value !== '' ? String(value) : block.cellFallback || '';
              appendTextWithPlaceholders(paragraph, cellValue);
              cell.append(paragraph);
              rowNode.append(cell);
            });
            tableNode.append(rowNode);
          });
          if (block.title) {
            root.append(createParagraph(block.title));
          }
          root.append(tableNode);
          if (block.caption) {
            appendLabeledLine(block.captionLabel || placeholderLabel, block.caption);
          }
          break;
        }
        case 'toc':
          root.append($createTableOfContentsNode());
          break;
        case 'map': {
          root.append($createReportVisualNode({
            ...block,
            visualType: 'map',
          }));
          break;
        }
        case 'chart': {
          root.append($createReportVisualNode({
            ...block,
            visualType: 'chart',
          }));
          break;
        }
        case 'image': {
          root.append($createReportVisualNode({
            ...block,
            visualType: 'image',
          }));
          break;
        }
        case 'placeholder':
          root.append(createPlaceholderBlock(block.text || ''));
          break;
        default:
          break;
      }
    });
  };

  const appendSection = (section) => {
    if (section?.isInternal) {
      return;
    }
    if (section.title) {
      const heading = $createHeadingAnchorNode(
        getHeadingTag(section.level),
        createAnchorId(section.id || section.title, usedAnchors)
      );
      heading.append($createTextNode(section.title));
      root.append(heading);
    }
    appendBlocks(section.blocks);
    (section.children || []).forEach((child) => appendSection(child));
  };

  templateDocument.sections.forEach((section) => appendSection(section));
  root.append(createParagraph(''));
};

function HeadingAnchorPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerNodeTransform(HeadingNode, (node) => {
      if ($isHeadingAnchorNode(node)) {
        if (!node.getAnchorId()) {
          node.setAnchorId(`section-${node.getKey()}`);
        }
        return;
      }
      const anchorNode = $createHeadingAnchorNode(node.getTag(), `section-${node.getKey()}`);
      anchorNode.append(...node.getChildren());
      node.replace(anchorNode);
    });
  }, [editor]);

  return null;
}

function HeadingNumberingPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      const headingNumbers = [];

      editorState.read(() => {
        const counters = [0, 0, 0];
        const visit = (node) => {
          if ($isHeadingAnchorNode(node) || $isHeadingNode(node)) {
            const level = getHeadingLevel(node.getTag?.());
            counters[level - 1] += 1;
            for (let index = level; index < counters.length; index += 1) {
              counters[index] = 0;
            }
            headingNumbers.push({
              key: node.getKey(),
              number: counters.slice(0, level).join('.'),
            });
          }
          if (typeof node.getChildren === 'function') {
            node.getChildren().forEach(visit);
          }
        };
        $getRoot().getChildren().forEach(visit);
      });

      const rootElement = editor.getRootElement();
      if (!rootElement) {
        return;
      }

      rootElement
        .querySelectorAll('.report-draft-editor__heading[data-heading-number]')
        .forEach((element) => {
          element.removeAttribute('data-heading-number');
        });

      headingNumbers.forEach(({ key, number }) => {
        const element = editor.getElementByKey(key);
        if (element) {
          element.setAttribute('data-heading-number', number);
        }
      });
    });
  }, [editor]);

  return null;
}

function AutosavePlugin({
  storageKey,
  locale,
  reportId,
  templateVersion,
  initialDraftPayload,
  onAutosave,
  onStatusChange,
  onRegisterRetry,
  onRegisterPersistenceControls,
  onRegisterConflictActions,
}) {
  const [editor] = useLexicalComposerContext();
  const localSaveTimeoutRef = useRef(null);
  const localSaveWatchdogRef = useRef(null);
  const localSaveSlowTimerRef = useRef(null);
  const remoteSyncTimeoutRef = useRef(null);
  const remoteRetryTimeoutRef = useRef(null);
  const lastActivityLogRef = useRef(0);
  const latestStateRef = useRef(null);
  const latestLocalPayloadRef = useRef(
    normalizeDraftPayload(initialDraftPayload, { reportId, locale }) || null
  );
  const lastLocallySavedSerializedRef = useRef(getSerializedEditorState(initialDraftPayload));
  const lastSyncedSerializedRef = useRef(
    initialDraftPayload?.sync?.status === 'synced'
      ? getSerializedEditorState(initialDraftPayload)
      : null
  );
  const pendingLocalSaveRef = useRef(false);
  const pendingRemoteSyncRef = useRef(Boolean(
    latestLocalPayloadRef.current
    && latestLocalPayloadRef.current.sync?.status !== 'synced'
  ));
  const isLocalSaveInFlightRef = useRef(false);
  const isRemoteSyncInFlightRef = useRef(false);
  const localSavePromiseRef = useRef(Promise.resolve(false));
  const remoteSyncPromiseRef = useRef(Promise.resolve(false));
  const dirtyRevisionRef = useRef(0);
  const isMountedRef = useRef(true);
  const localSavingStartedAtRef = useRef(null);
  const clientInstanceIdRef = useRef(
    initialDraftPayload?.clientInstanceId || createDraftClientInstanceId()
  );
  const clientRevisionRef = useRef(
    Number.isFinite(Number(initialDraftPayload?.clientRevision))
      ? Number(initialDraftPayload.clientRevision)
      : 0
  );
  const serverRevisionRef = useRef(
    Number.isFinite(Number(initialDraftPayload?.sync?.serverRevision))
      ? Number(initialDraftPayload.sync.serverRevision)
      : null
  );
  const remoteRetryIndexRef = useRef(0);
  const remoteSyncBlockedByConflictRef = useRef(false);
  const conflictServerDraftRef = useRef(null);
  const suppressOnChangeRef = useRef(false);
  const flushLocalNowRef = useRef(async () => false);
  const flushRemoteNowRef = useRef(async () => false);
  const scheduleRemoteSyncRef = useRef(() => {});

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (localSaveTimeoutRef.current) {
        clearTimeout(localSaveTimeoutRef.current);
      }
      if (localSaveWatchdogRef.current) {
        clearTimeout(localSaveWatchdogRef.current);
      }
      if (localSaveSlowTimerRef.current) {
        clearTimeout(localSaveSlowTimerRef.current);
      }
      if (remoteSyncTimeoutRef.current) {
        clearTimeout(remoteSyncTimeoutRef.current);
      }
      if (remoteRetryTimeoutRef.current) {
        clearTimeout(remoteRetryTimeoutRef.current);
      }
    };
  }, []);

  const clearLocalSlowSavingTimer = useCallback(() => {
    if (localSaveSlowTimerRef.current) {
      clearTimeout(localSaveSlowTimerRef.current);
      localSaveSlowTimerRef.current = null;
    }
  }, []);

  const clearLocalSaveWatchdog = useCallback(() => {
    if (localSaveWatchdogRef.current) {
      clearTimeout(localSaveWatchdogRef.current);
      localSaveWatchdogRef.current = null;
    }
  }, []);

  const clearRemoteTimers = useCallback(() => {
    if (remoteSyncTimeoutRef.current) {
      clearTimeout(remoteSyncTimeoutRef.current);
      remoteSyncTimeoutRef.current = null;
    }
    if (remoteRetryTimeoutRef.current) {
      clearTimeout(remoteRetryTimeoutRef.current);
      remoteRetryTimeoutRef.current = null;
    }
  }, []);

  const isOffline = useCallback(() => (
    typeof navigator !== 'undefined' && navigator.onLine === false
  ), []);

  const emitCapturedStatus = useCallback(() => {
    if (remoteSyncBlockedByConflictRef.current) {
      onStatusChange('conflict', {
        message: 'Draft sync conflict. Resolve before syncing.',
        serverDraft: conflictServerDraftRef.current,
      });
      return;
    }
    onStatusChange('captured', { message: null, isSlow: false });
  }, [onStatusChange]);

  const beginLocalSavingStatus = useCallback(() => {
    const startedAt = new Date();
    localSavingStartedAtRef.current = startedAt;
    clearLocalSlowSavingTimer();
    onStatusChange('saving_local', {
      startedAt,
      phase: 'local',
      isSlow: false,
      message: null,
    });
    localSaveSlowTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }
      if (localSavingStartedAtRef.current !== startedAt) {
        return;
      }
      onStatusChange('saving_local', {
        startedAt,
        phase: 'local',
        isSlow: true,
        message: null,
      });
    }, AUTOSAVE_SLOW_STATUS_MS);
  }, [clearLocalSlowSavingTimer, onStatusChange]);

  const emitPostLocalSaveStatus = useCallback((payload) => {
    const savedAt = payload?.timestamp ? new Date(payload.timestamp) : new Date();
    if (remoteSyncBlockedByConflictRef.current) {
      onStatusChange('conflict', {
        savedAt,
        message: 'Draft sync conflict. Resolve before syncing.',
        serverDraft: conflictServerDraftRef.current,
      });
      return;
    }
    if (isOffline()) {
      onStatusChange('saved_local_offline', { savedAt });
      return;
    }
    onStatusChange('saved_local_syncing_remote', { savedAt });
  }, [isOffline, onStatusChange]);

  const emitLocalError = useCallback((error) => {
    onStatusChange('error', {
      errorCode: typeof error?.code === 'string' ? error.code : null,
      message:
        error instanceof Error
          ? error.message
          : 'Autosave failed. Please retry.',
    });
  }, [onStatusChange]);

  const queueRemoteRetry = useCallback(async (payload, error) => {
    const currentDelay = REMOTE_SYNC_RETRY_DELAYS_MS[
      Math.min(remoteRetryIndexRef.current, REMOTE_SYNC_RETRY_DELAYS_MS.length - 1)
    ];
    remoteRetryIndexRef.current = Math.min(
      remoteRetryIndexRef.current + 1,
      REMOTE_SYNC_RETRY_DELAYS_MS.length - 1
    );
    const retryAtDate = new Date(Date.now() + currentDelay);

    let nextLocalPayload = payload;
    try {
      const updated = await markSyncError({
        storageKey,
        payload,
        status: isOffline() ? 'saved_local_offline' : 'sync_error',
        errorCode: typeof error?.code === 'string' ? error.code : 'sync_error',
        message:
          error instanceof Error
            ? error.message
            : 'Background sync failed.',
        retryAt: retryAtDate.toISOString(),
      });
      if (updated) {
        nextLocalPayload = updated;
        latestLocalPayloadRef.current = updated;
      }
    } catch (markError) {
      // Ignore local metadata update failures; main local content is already saved.
    }

    if (isOffline()) {
      onStatusChange('saved_local_offline', {
        savedAt: nextLocalPayload?.timestamp ? new Date(nextLocalPayload.timestamp) : new Date(),
        retryAt: retryAtDate,
      });
    } else {
      onStatusChange('sync_error', {
        errorCode: typeof error?.code === 'string' ? error.code : 'sync_error',
        message: error instanceof Error ? error.message : 'Background sync failed.',
        retryAt: retryAtDate,
      });
    }

    if (remoteSyncBlockedByConflictRef.current) {
      return;
    }
    if (remoteRetryTimeoutRef.current) {
      clearTimeout(remoteRetryTimeoutRef.current);
    }
    remoteRetryTimeoutRef.current = setTimeout(() => {
      remoteRetryTimeoutRef.current = null;
      pendingRemoteSyncRef.current = true;
      void flushRemoteNowRef.current({ reason: 'retry' });
    }, currentDelay);
  }, [isOffline, onStatusChange, storageKey]);

  const markNonRetryableSyncError = useCallback(async (payload, error) => {
    let nextLocalPayload = payload;
    try {
      const updated = await markSyncError({
        storageKey,
        payload,
        status: 'sync_error',
        errorCode: typeof error?.code === 'string' ? error.code : 'sync_error',
        message: error instanceof Error ? error.message : 'Background sync failed.',
        retryAt: null,
      });
      if (updated) {
        nextLocalPayload = updated;
        latestLocalPayloadRef.current = updated;
      }
    } catch (markError) {
      // Ignore local metadata write failures.
    }
    onStatusChange('sync_error', {
      errorCode: typeof error?.code === 'string' ? error.code : 'sync_error',
      message: error instanceof Error ? error.message : 'Background sync failed.',
      savedAt: nextLocalPayload?.timestamp ? new Date(nextLocalPayload.timestamp) : new Date(),
    });
  }, [onStatusChange, storageKey]);

  const serializeLatestEditorState = useCallback(() => {
    const editorState = latestStateRef.current;
    if (!editorState) {
      return null;
    }
    const editorStateJson = editorState.toJSON();
    return {
      editorStateJson,
      serializedEditorState: JSON.stringify(editorStateJson),
    };
  }, []);

  const flushLocalNow = useCallback(async (options = {}) => {
    if (!storageKey || typeof window === 'undefined') {
      pendingLocalSaveRef.current = false;
      emitLocalError(Object.assign(new Error('Autosave unavailable.'), { code: 'storage_unavailable' }));
      return false;
    }

    if (isLocalSaveInFlightRef.current) {
      return localSavePromiseRef.current;
    }

    if (localSaveTimeoutRef.current) {
      clearTimeout(localSaveTimeoutRef.current);
      localSaveTimeoutRef.current = null;
    }

    const hasPendingLocalSave = pendingLocalSaveRef.current;
    const existingPayload = latestLocalPayloadRef.current;
    if (!hasPendingLocalSave && existingPayload) {
      return true;
    }
    if (!hasPendingLocalSave && !latestStateRef.current) {
      return false;
    }

    isLocalSaveInFlightRef.current = true;
    const localSavePromise = (async () => {
      try {
        const flushStartedAtMs = Date.now();
        beginLocalSavingStatus();
        const timeoutPromise = new Promise((_, reject) => {
          clearLocalSaveWatchdog();
          localSaveWatchdogRef.current = setTimeout(() => {
            reject(createAutosaveTimeoutError());
          }, AUTOSAVE_SAVE_TIMEOUT_MS);
        });

        const workPromise = (async () => {
          let serializedEditorState = existingPayload?.editorState || null;
          if (pendingLocalSaveRef.current || !serializedEditorState) {
            const serialization = serializeLatestEditorState();
            if (!serialization) {
              throw new Error('Nothing to save yet. Continue editing and retry.');
            }
            serializedEditorState = serialization.serializedEditorState;
          }

          const savedAt = new Date();
          const nextPayload = normalizeDraftPayload({
            ...(latestLocalPayloadRef.current || {}),
            reportId,
            locale,
            templateVersion: templateVersion || null,
            timestamp: savedAt.toISOString(),
            editorState: serializedEditorState,
            clientInstanceId: clientInstanceIdRef.current,
            clientRevision: clientRevisionRef.current,
            sync: {
              ...(latestLocalPayloadRef.current?.sync || {}),
              status: remoteSyncBlockedByConflictRef.current
                ? 'conflict'
                : (latestLocalPayloadRef.current?.sync?.status || 'local_only'),
              lastErrorCode: remoteSyncBlockedByConflictRef.current
                ? 'conflict'
                : (latestLocalPayloadRef.current?.sync?.lastErrorCode || null),
              lastErrorMessage: remoteSyncBlockedByConflictRef.current
                ? 'Draft sync conflict. Resolve before syncing.'
                : (latestLocalPayloadRef.current?.sync?.lastErrorMessage || null),
            },
          });
          const saveResult = await saveLocalDraftSnapshot({
            storageKey,
            payload: nextPayload,
          });
          return {
            savedAt,
            payload: saveResult.payload,
            backend: saveResult.backend,
            totalLocalMs: Date.now() - flushStartedAtMs,
          };
        })();

        const localResult = await Promise.race([workPromise, timeoutPromise]);
        clearLocalSaveWatchdog();
        clearLocalSlowSavingTimer();
        localSavingStartedAtRef.current = null;

        pendingLocalSaveRef.current = false;
        latestLocalPayloadRef.current = localResult.payload;
        lastLocallySavedSerializedRef.current = localResult.payload.editorState;

        if (remoteSyncBlockedByConflictRef.current) {
          pendingRemoteSyncRef.current = false;
        } else {
          pendingRemoteSyncRef.current = true;
        }

        emitPostLocalSaveStatus(localResult.payload);

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.info('[ReportDraftAutosave] local-flush', {
            reportId,
            locale,
            backend: localResult.backend,
            localMs: localResult.totalLocalMs,
            payloadBytes: localResult.payload?.editorState?.length || null,
            clientRevision: localResult.payload?.clientRevision || null,
          });
        }

        if (
          typeof onAutosave === 'function'
          && Date.now() - lastActivityLogRef.current > ACTIVITY_LOG_THROTTLE_MS
        ) {
          onAutosave(localResult.payload);
          lastActivityLogRef.current = Date.now();
        }

        if (!remoteSyncBlockedByConflictRef.current && options.skipRemoteSchedule !== true) {
          scheduleRemoteSyncRef.current({ immediate: false, reason: options.reason || 'local-save' });
        }
        return true;
      } catch (error) {
        clearLocalSaveWatchdog();
        clearLocalSlowSavingTimer();
        localSavingStartedAtRef.current = null;
        pendingLocalSaveRef.current = false;
        emitLocalError(error);
        return false;
      } finally {
        isLocalSaveInFlightRef.current = false;
      }
    })();
    localSavePromiseRef.current = localSavePromise;
    return localSavePromise;
  }, [
    beginLocalSavingStatus,
    clearLocalSaveWatchdog,
    clearLocalSlowSavingTimer,
    emitLocalError,
    emitPostLocalSaveStatus,
    locale,
    onAutosave,
    reportId,
    serializeLatestEditorState,
      storageKey,
      templateVersion,
  ]);

  const flushRemoteNow = useCallback(async ({ force = false } = {}) => {
    if (isRemoteSyncInFlightRef.current) {
      pendingRemoteSyncRef.current = true;
      return remoteSyncPromiseRef.current;
    }
    if (remoteSyncBlockedByConflictRef.current && !force) {
      return false;
    }

    clearRemoteTimers();

    if (!latestLocalPayloadRef.current) {
      await flushLocalNowRef.current({ reason: 'remote-prep', skipRemoteSchedule: true });
    }

    const localPayload = latestLocalPayloadRef.current;
    if (!localPayload) {
      return false;
    }

    if (!reportId || !locale) {
      onStatusChange('saved', {
        savedAt: localPayload.timestamp ? new Date(localPayload.timestamp) : new Date(),
      });
      return true;
    }

    const localEditorState = localPayload.editorState;
    const localServerRevision = Number.isFinite(Number(localPayload.sync?.serverRevision))
      ? Number(localPayload.sync.serverRevision)
      : serverRevisionRef.current;

    if (!force && localEditorState && lastSyncedSerializedRef.current === localEditorState) {
      try {
        const marked = await markSyncSuccess({
          storageKey,
          payload: localPayload,
          serverDraft: {
            serverRevision: localServerRevision,
            updatedAt: localPayload.sync?.lastSyncedAt || localPayload.timestamp,
          },
        });
        if (marked) {
          latestLocalPayloadRef.current = marked;
        }
      } catch (error) {
        // Ignore local metadata write failures.
      }
      onStatusChange('saved', {
        savedAt: localPayload.timestamp ? new Date(localPayload.timestamp) : new Date(),
      });
      pendingRemoteSyncRef.current = false;
      return true;
    }

    if (isOffline()) {
      pendingRemoteSyncRef.current = true;
      await queueRemoteRetry(localPayload, Object.assign(new Error('Offline. Sync will retry automatically.'), {
        code: 'offline',
      }));
      return false;
    }

    if (!pendingRemoteSyncRef.current && !force) {
      return true;
    }

    isRemoteSyncInFlightRef.current = true;
    const remotePromise = (async () => {
      const remoteStartedAtMs = Date.now();
      onStatusChange('saved_local_syncing_remote', {
        savedAt: localPayload.timestamp ? new Date(localPayload.timestamp) : new Date(),
      });
      try {
        const { serverDraft } = await syncDraftToServer({
          reportId,
          locale,
          payload: localPayload,
          baseServerRevision: force
            ? localServerRevision
            : (serverRevisionRef.current ?? localServerRevision ?? null),
          force,
        });

        serverRevisionRef.current = Number(serverDraft?.serverRevision || 1);
        lastSyncedSerializedRef.current = localPayload.editorState;
        remoteRetryIndexRef.current = 0;
        remoteSyncBlockedByConflictRef.current = false;
        conflictServerDraftRef.current = null;
        pendingRemoteSyncRef.current = false;

        try {
          const marked = await markSyncSuccess({
            storageKey,
            payload: localPayload,
            serverDraft,
          });
          if (marked) {
            latestLocalPayloadRef.current = marked;
          }
        } catch (error) {
          // Ignore local metadata write failures after successful server sync.
        }

        onStatusChange('saved', {
          savedAt: localPayload.timestamp ? new Date(localPayload.timestamp) : new Date(),
          serverRevision: serverDraft?.serverRevision || null,
        });

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.info('[ReportDraftAutosave] remote-sync', {
            reportId,
            locale,
            remoteMs: Date.now() - remoteStartedAtMs,
            syncStatus: 'saved',
            serverRevision: serverDraft?.serverRevision || null,
          });
        }
        return true;
      } catch (error) {
        pendingRemoteSyncRef.current = true;
        if (error?.code === 'conflict') {
          remoteSyncBlockedByConflictRef.current = true;
          conflictServerDraftRef.current = error.serverDraft || null;
          try {
            const markedConflict = await markSyncError({
              storageKey,
              payload: localPayload,
              status: 'conflict',
              errorCode: 'conflict',
              message: error.message || 'Draft sync conflict.',
              retryAt: null,
            });
            if (markedConflict) {
              latestLocalPayloadRef.current = markedConflict;
            }
          } catch (markError) {
            // Ignore local metadata write failures.
          }
          onStatusChange('conflict', {
            message: error.message || 'Draft sync conflict.',
            serverDraft: error.serverDraft || null,
            savedAt: localPayload.timestamp ? new Date(localPayload.timestamp) : new Date(),
          });
          pendingRemoteSyncRef.current = false;
          return false;
        }

        const shouldRetry = error?.code === 'network_error'
          || error?.code === 'offline'
          || Number(error?.status) >= 500;
        if (shouldRetry) {
          await queueRemoteRetry(localPayload, error);
        } else {
          pendingRemoteSyncRef.current = false;
          await markNonRetryableSyncError(localPayload, error);
        }
        return false;
      } finally {
        isRemoteSyncInFlightRef.current = false;
        if (pendingRemoteSyncRef.current && !remoteSyncBlockedByConflictRef.current) {
          scheduleRemoteSyncRef.current({ immediate: false, reason: 'follow-up' });
        }
      }
    })();
    remoteSyncPromiseRef.current = remotePromise;
    return remotePromise;
  }, [
    clearRemoteTimers,
    isOffline,
    locale,
    markNonRetryableSyncError,
    onStatusChange,
    queueRemoteRetry,
    reportId,
    storageKey,
  ]);

  const scheduleRemoteSync = useCallback(({ immediate = false } = {}) => {
    if (remoteSyncBlockedByConflictRef.current) {
      return;
    }
    pendingRemoteSyncRef.current = true;
    if (remoteSyncTimeoutRef.current) {
      clearTimeout(remoteSyncTimeoutRef.current);
      remoteSyncTimeoutRef.current = null;
    }
    if (isRemoteSyncInFlightRef.current) {
      return;
    }
    const delay = immediate ? 0 : REMOTE_SYNC_DEBOUNCE_MS;
    remoteSyncTimeoutRef.current = setTimeout(() => {
      remoteSyncTimeoutRef.current = null;
      void flushRemoteNowRef.current({ reason: immediate ? 'immediate' : 'debounce' });
    }, delay);
  }, []);

  flushLocalNowRef.current = flushLocalNow;
  flushRemoteNowRef.current = flushRemoteNow;
  scheduleRemoteSyncRef.current = scheduleRemoteSync;

  const flushAllNow = useCallback(async (options = {}) => {
    await flushLocalNow({ reason: options.reason || 'flush-all' });
    if (options.remote === false) {
      return;
    }
    void flushRemoteNow({ force: options.force === true });
  }, [flushLocalNow, flushRemoteNow]);

  const retrySave = useCallback(() => {
    if (localSaveTimeoutRef.current) {
      clearTimeout(localSaveTimeoutRef.current);
      localSaveTimeoutRef.current = null;
    }
    pendingLocalSaveRef.current = Boolean(latestStateRef.current);
    pendingRemoteSyncRef.current = true;
    remoteSyncBlockedByConflictRef.current = false;
    conflictServerDraftRef.current = null;
    emitCapturedStatus();
    void flushAllNow({ reason: 'manual-retry' });
  }, [emitCapturedStatus, flushAllNow]);

  useEffect(() => {
    if (typeof onRegisterRetry !== 'function') {
      return undefined;
    }
    onRegisterRetry(retrySave);
    return () => {
      onRegisterRetry(null);
    };
  }, [onRegisterRetry, retrySave]);

  const applyServerDraftToEditor = useCallback(async () => {
    const serverDraft = conflictServerDraftRef.current;
    if (!serverDraft || typeof serverDraft.editorState !== 'string') {
      return false;
    }

    let nextEditorState = null;
    try {
      nextEditorState = editor.parseEditorState(serverDraft.editorState);
    } catch (error) {
      onStatusChange('conflict', {
        message: 'Could not load the server draft into the editor.',
        serverDraft,
      });
      return false;
    }

    suppressOnChangeRef.current = true;
    editor.setEditorState(nextEditorState);
    latestStateRef.current = nextEditorState;

    let replacedLocalPayload = latestLocalPayloadRef.current;
    try {
      const nextLocal = await replaceLocalWithServerDraft({
        storageKey,
        localDraft: latestLocalPayloadRef.current,
        serverDraft,
      });
      if (nextLocal) {
        replacedLocalPayload = nextLocal;
      }
    } catch (error) {
      // If local metadata write fails, keep editor state updated and continue.
    }

    latestLocalPayloadRef.current = replacedLocalPayload;
    lastLocallySavedSerializedRef.current = serverDraft.editorState;
    lastSyncedSerializedRef.current = serverDraft.editorState;
    serverRevisionRef.current = Number.isFinite(Number(serverDraft.serverRevision))
      ? Number(serverDraft.serverRevision)
      : serverRevisionRef.current;
    pendingLocalSaveRef.current = false;
    pendingRemoteSyncRef.current = false;
    remoteSyncBlockedByConflictRef.current = false;
    conflictServerDraftRef.current = null;
    remoteRetryIndexRef.current = 0;

    onStatusChange('saved', {
      savedAt: replacedLocalPayload?.timestamp
        ? new Date(replacedLocalPayload.timestamp)
        : new Date(),
      serverRevision: serverDraft.serverRevision || null,
    });
    return true;
  }, [editor, onStatusChange, storageKey]);

  const overwriteServerDraft = useCallback(async () => {
    remoteSyncBlockedByConflictRef.current = false;
    pendingRemoteSyncRef.current = true;
    emitPostLocalSaveStatus(latestLocalPayloadRef.current);
    return flushRemoteNow({ force: true });
  }, [emitPostLocalSaveStatus, flushRemoteNow]);

  useEffect(() => {
    if (typeof onRegisterPersistenceControls !== 'function') {
      return undefined;
    }
    onRegisterPersistenceControls({
      flushLocalNow,
      flushRemoteNow,
      flushAllNow,
    });
    return () => {
      onRegisterPersistenceControls(null);
    };
  }, [flushAllNow, flushLocalNow, flushRemoteNow, onRegisterPersistenceControls]);

  useEffect(() => {
    if (typeof onRegisterConflictActions !== 'function') {
      return undefined;
    }
    onRegisterConflictActions({
      useServerDraft: applyServerDraftToEditor,
      overwriteServerDraft,
    });
    return () => {
      onRegisterConflictActions(null);
    };
  }, [applyServerDraftToEditor, onRegisterConflictActions, overwriteServerDraft]);

  useEffect(() => {
    if (pendingRemoteSyncRef.current && !remoteSyncBlockedByConflictRef.current) {
      scheduleRemoteSync({ immediate: false });
    }
  }, [scheduleRemoteSync]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const handlePageHide = () => {
      void flushLocalNowRef.current({ reason: 'pagehide' });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void flushLocalNowRef.current({ reason: 'hidden' });
      }
    };
    const handleOnline = () => {
      if (remoteSyncBlockedByConflictRef.current) {
        return;
      }
      remoteRetryIndexRef.current = 0;
      pendingRemoteSyncRef.current = true;
      scheduleRemoteSyncRef.current({ immediate: true });
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    let currentRoot = null;
    const handleBlur = () => {
      void flushLocalNowRef.current({ reason: 'blur' });
    };

    const unregister = editor.registerRootListener((rootElement, prevRootElement) => {
      if (prevRootElement) {
        prevRootElement.removeEventListener('blur', handleBlur, true);
      }
      if (rootElement) {
        rootElement.addEventListener('blur', handleBlur, true);
      }
      currentRoot = rootElement || null;
    });

    return () => {
      if (currentRoot) {
        currentRoot.removeEventListener('blur', handleBlur, true);
      }
      unregister();
    };
  }, [editor]);

  const handleChange = useCallback(
    (editorState) => {
      if (suppressOnChangeRef.current) {
        suppressOnChangeRef.current = false;
        return;
      }
      latestStateRef.current = editorState;
      dirtyRevisionRef.current += 1;
      clientRevisionRef.current += 1;
      pendingLocalSaveRef.current = true;
      pendingRemoteSyncRef.current = true;
      clearRemoteTimers();
      if (!remoteSyncBlockedByConflictRef.current) {
        emitCapturedStatus();
      }
      if (localSaveTimeoutRef.current) {
        clearTimeout(localSaveTimeoutRef.current);
      }
      localSaveTimeoutRef.current = setTimeout(() => {
        localSaveTimeoutRef.current = null;
        void flushLocalNowRef.current({ reason: 'debounce' });
      }, LOCAL_AUTOSAVE_DEBOUNCE_MS);
    },
    [clearRemoteTimers, emitCapturedStatus]
  );

  return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />;
}

function WordCountPlugin({ onWordCountChange }) {
  const [editor] = useLexicalComposerContext();
  const lastCountRef = useRef(0);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const text = $getRoot().getTextContent().trim();
        const nextCount = text ? text.split(/\s+/).length : 0;
        if (nextCount !== lastCountRef.current) {
          lastCountRef.current = nextCount;
          if (typeof onWordCountChange === 'function') {
            onWordCountChange(nextCount);
          }
        }
      });
    });
  }, [editor, onWordCountChange]);

  return null;
}

function AIIslandPlugin({ isExpanded, onExpandedChange }) {
  const [editor] = useLexicalComposerContext();
  return <AIIsland editor={editor} expanded={isExpanded} onExpandedChange={onExpandedChange} />;
}

const buildDynamicAnchorId = () => (
  `section-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
);

const getSelectionTopLevelElement = (selection) => {
  const anchorNode = selection?.anchor?.getNode?.();
  if (!anchorNode || anchorNode.getType?.() === 'root') {
    return null;
  }
  try {
    return anchorNode.getTopLevelElementOrThrow();
  } catch (error) {
    return null;
  }
};

const resolveSelectionBlockType = (selection) => {
  const topLevel = getSelectionTopLevelElement(selection);
  if (!topLevel) {
    return 'paragraph';
  }
  if ($isHeadingNode(topLevel)) {
    return topLevel.getTag();
  }
  if ($isQuoteNode(topLevel)) {
    return 'quote';
  }
  if ($isListNode(topLevel)) {
    return topLevel.getListType();
  }
  if (topLevel.getType?.() === 'listitem') {
    const listParent = topLevel.getParent();
    if ($isListNode(listParent)) {
      return listParent.getListType();
    }
  }
  return 'paragraph';
};

const getStyleLabelKeyByBlockType = (blockType) => {
  if (blockType === 'h1') return 'heading1';
  if (blockType === 'h2') return 'heading2';
  if (blockType === 'h3') return 'heading3';
  if (blockType === 'quote') return 'quote';
  if (blockType === 'bullet') return 'bullets';
  if (blockType === 'number') return 'numbered';
  return 'paragraph';
};

const buildDefaultChartData = () => ([
  { label: 'Apr', value: 3.2 },
  { label: 'May', value: 3.0 },
  { label: 'Jun', value: 2.7 },
  { label: 'Jul', value: 2.3 },
  { label: 'Aug', value: 2.1 },
]);

const cloneSerializable = (value) => {
  if (value == null) {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

export const buildRangeSelectionSnapshot = (selection) => {
  if (!$isRangeSelection(selection)) {
    return null;
  }
  return {
    anchorKey: selection.anchor.key,
    anchorOffset: selection.anchor.offset,
    anchorType: selection.anchor.type || 'text',
    focusKey: selection.focus.key,
    focusOffset: selection.focus.offset,
    focusType: selection.focus.type || 'text',
  };
};

export const restoreRangeSelectionSnapshot = (snapshot) => {
  if (!snapshot) {
    return false;
  }
  const anchorNode = $getNodeByKey(snapshot.anchorKey);
  const focusNode = $getNodeByKey(snapshot.focusKey);
  if (!anchorNode || !focusNode) {
    return false;
  }
  const selection = $createRangeSelection();
  selection.anchor.set(anchorNode.getKey(), snapshot.anchorOffset, snapshot.anchorType || 'text');
  selection.focus.set(focusNode.getKey(), snapshot.focusOffset, snapshot.focusType || 'text');
  $setSelection(selection);
  return true;
};

const applyBlockStyle = (editor, styleId) => {
  if (styleId === 'bullet' || styleId === 'number') {
    let shouldRemoveList = false;
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }
      const blockType = resolveSelectionBlockType(selection);
      shouldRemoveList = blockType === styleId;
    });
    if (shouldRemoveList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      return;
    }
    editor.dispatchCommand(
      styleId === 'number' ? INSERT_ORDERED_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
      undefined
    );
    return;
  }

  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }
    if (styleId === 'paragraph') {
      $setBlocksType(selection, () => $createParagraphNode());
      return;
    }
    if (styleId === 'quote') {
      $setBlocksType(selection, () => $createQuoteNode());
      return;
    }
    if (styleId === 'h1' || styleId === 'h2' || styleId === 'h3') {
      $setBlocksType(selection, () => $createHeadingAnchorNode(styleId, buildDynamicAnchorId()));
    }
  });
};

const insertManualPlaceholder = (editor, label) => {
  const resolvedLabel = String(label || '').trim();
  editor.update(() => {
    const placeholder = $createPlaceholderNode();
    placeholder.append($createTextNode(resolvedLabel || 'Manual note placeholder'));
    $insertNodes([placeholder]);
  });
};

const insertVisualBlock = (editor, type, t) => {
  const visualType = type === 'map' ? 'map' : 'chart';
  const nodeData = buildBlankVisualNodeData({ visualType, t });
  editor.update(() => {
    $insertNodes([$createReportVisualNode(nodeData)]);
  });
};

const collectHeadingEntries = () => {
  const entries = [];
  const counters = [0, 0, 0];
  const visit = (node) => {
    if ($isHeadingAnchorNode(node) || $isHeadingNode(node)) {
      const level = getHeadingLevel(node.getTag?.());
      counters[level - 1] += 1;
      for (let index = level; index < counters.length; index += 1) {
        counters[index] = 0;
      }
      entries.push({
        key: node.getKey(),
        anchorId: typeof node.getAnchorId === 'function' ? node.getAnchorId() : null,
        level,
        number: counters.slice(0, level).join('.'),
        label: node.getTextContent().trim(),
      });
    }
    if (typeof node.getChildren === 'function') {
      node.getChildren().forEach(visit);
    }
  };
  $getRoot().getChildren().forEach(visit);
  return entries;
};

const areHeadingEntriesEqual = (nextEntries, prevEntries) => {
  if (nextEntries === prevEntries) {
    return true;
  }
  if (!Array.isArray(nextEntries) || !Array.isArray(prevEntries)) {
    return false;
  }
  if (nextEntries.length !== prevEntries.length) {
    return false;
  }
  for (let index = 0; index < nextEntries.length; index += 1) {
    const next = nextEntries[index];
    const prev = prevEntries[index];
    if (
      next?.key !== prev?.key
      || next?.anchorId !== prev?.anchorId
      || next?.level !== prev?.level
      || next?.number !== prev?.number
      || next?.label !== prev?.label
    ) {
      return false;
    }
  }
  return true;
};

function OptionalPortal({ target, children }) {
  if (!target) {
    return children;
  }
  return createPortal(children, target);
}

OptionalPortal.propTypes = {
  target: PropTypes.any,
  children: PropTypes.node,
};

OptionalPortal.defaultProps = {
  target: null,
  children: null,
};

function ReportDraftToolbar({
  status,
  lastSavedAt,
  lastErrorMessage,
  isSlowSaving,
  onRetrySave,
  onUseServerDraft,
  onOverwriteServerDraft,
  locale,
  toolbarPortalTarget,
  templateDocument,
  visualDragBridgeRef,
}) {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const {
    hourlyData,
    dailyData,
    selectedFarm,
    selectedSections,
    timeRange,
  } = useAnalytics();
  const [blockType, setBlockType] = useState('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isHighlight, setIsHighlight] = useState(false);
  const [alignment, setAlignment] = useState('left');
  const [headingEntries, setHeadingEntries] = useState([]);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isFindReplaceOpen, setIsFindReplaceOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [replaceValue, setReplaceValue] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [findState, setFindState] = useState({
    matchCount: 0,
    currentMatch: 0,
    replacedCount: 0,
  });
  const [isVisualPickerOpen, setIsVisualPickerOpen] = useState(false);
  const [isVisualPickerTemporarilyHidden, setIsVisualPickerTemporarilyHidden] = useState(false);
  const [visualPickerMode, setVisualPickerMode] = useState('chart');
  const [isCompactToolbar, setIsCompactToolbar] = useState(false);
  const [toolbarWidth, setToolbarWidth] = useState(0);
  const toolbarRef = useRef(null);
  const toolbarBarRef = useRef(null);
  const toolbarLeftRef = useRef(null);
  const findReplaceRef = useRef(null);
  const toolbarLastMeasuredWidthRef = useRef(0);
  const toolbarMeasureRafRef = useRef(null);
  const toolbarCompactBreakWidthRef = useRef(0);
  const visualPickerDragHideRafRef = useRef(null);
  const latestRangeSelectionSnapshotRef = useRef(null);
  const visualPickerSelectionSnapshotRef = useRef(null);
  const isVisualDragSessionActiveRef = useRef(false);
  const didVisualDragDropSucceedRef = useRef(false);
  const activeDraggedVisualItemRef = useRef(null);
  const advancedEditorEnabled = ADVANCED_REPORT_EDITOR_ENABLED;
  const shortcuts = useMemo(() => ({
    undo: t('reports.review.editor.shortcuts.undo'),
    redo: t('reports.review.editor.shortcuts.redo'),
    paragraph: t('reports.review.editor.shortcuts.paragraph'),
    heading1: t('reports.review.editor.shortcuts.heading1'),
    heading2: t('reports.review.editor.shortcuts.heading2'),
    heading3: t('reports.review.editor.shortcuts.heading3'),
    bold: t('reports.review.editor.shortcuts.bold'),
    italic: t('reports.review.editor.shortcuts.italic'),
    underline: t('reports.review.editor.shortcuts.underline'),
    strikethrough: t('reports.review.editor.shortcuts.strikethrough'),
    code: t('reports.review.editor.shortcuts.code'),
    highlight: t('reports.review.editor.shortcuts.highlight'),
    alignLeft: t('reports.review.editor.shortcuts.alignLeft'),
    alignCenter: t('reports.review.editor.shortcuts.alignCenter'),
    alignRight: t('reports.review.editor.shortcuts.alignRight'),
    alignJustify: t('reports.review.editor.shortcuts.alignJustify'),
    indent: t('reports.review.editor.shortcuts.indent'),
    outdent: t('reports.review.editor.shortcuts.outdent'),
    bullets: t('reports.review.editor.shortcuts.bullets'),
    numbered: t('reports.review.editor.shortcuts.numbered'),
    link: t('reports.review.editor.shortcuts.link'),
    table: t('reports.review.editor.shortcuts.table'),
    image: t('reports.review.editor.shortcuts.image'),
    quote: t('reports.review.editor.shortcuts.quote'),
    command: t('reports.review.editor.shortcuts.commandPalette', { defaultValue: 'Cmd/Ctrl+K' }),
    find: t('reports.review.editor.shortcuts.findReplace', { defaultValue: 'Cmd/Ctrl+F' }),
  }), [t]);
  const labels = useMemo(() => ({
    undo: t('reports.review.editor.toolbar.undo'),
    redo: t('reports.review.editor.toolbar.redo'),
    paragraph: t('reports.review.editor.toolbar.paragraph'),
    heading1: t('reports.review.editor.toolbar.heading1'),
    heading2: t('reports.review.editor.toolbar.heading2'),
    heading3: t('reports.review.editor.toolbar.heading3'),
    bold: t('reports.review.editor.toolbar.bold'),
    italic: t('reports.review.editor.toolbar.italic'),
    underline: t('reports.review.editor.toolbar.underline'),
    strikethrough: t('reports.review.editor.toolbar.strikethrough'),
    code: t('reports.review.editor.toolbar.code'),
    highlight: t('reports.review.editor.toolbar.highlight'),
    alignLeft: t('reports.review.editor.toolbar.alignLeft'),
    alignCenter: t('reports.review.editor.toolbar.alignCenter'),
    alignRight: t('reports.review.editor.toolbar.alignRight'),
    alignJustify: t('reports.review.editor.toolbar.alignJustify'),
    indent: t('reports.review.editor.toolbar.indent'),
    outdent: t('reports.review.editor.toolbar.outdent'),
    bullets: t('reports.review.editor.toolbar.bullets'),
    numbered: t('reports.review.editor.toolbar.numbered'),
    table: t('reports.review.editor.toolbar.table'),
    link: t('reports.review.editor.toolbar.link'),
    image: t('reports.review.editor.toolbar.image'),
    quote: t('reports.review.editor.toolbar.quote'),
    styles: t('reports.review.editor.styles.menu'),
    insert: t('reports.review.editor.insert.menu'),
    findReplace: t('reports.review.editor.findReplace.title'),
    commandPalette: t('reports.review.editor.commandPalette.menu'),
    more: t('reports.review.editor.toolbar.more'),
  }), [t]);

  const getTooltip = useCallback((label, shortcut) => (
    shortcut ? `${label} (${shortcut})` : label
  ), []);

  const preventToolbarMouseDown = useCallback((event) => {
    event.preventDefault();
  }, []);

  const runToolbarCommand = useCallback((action, options = {}) => {
    if (typeof action !== 'function') {
      return;
    }
    const {
      focusEditor = true,
      restoreSelection = true,
    } = options;
    const rootElement = editor.getRootElement();
    const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
    const isEditorFocused = Boolean(
      rootElement
      && activeElement
      && (activeElement === rootElement || rootElement.contains(activeElement))
    );

    if (focusEditor && !isEditorFocused) {
      editor.focus();
    }

    if (restoreSelection && !isEditorFocused && latestRangeSelectionSnapshotRef.current) {
      const snapshot = latestRangeSelectionSnapshotRef.current;
      editor.update(() => {
        restoreRangeSelectionSnapshot(snapshot);
      });
    }

    action();
  }, [editor]);

  const runMutatingToolbarCommand = useCallback((action, options = {}) => {
    runToolbarCommand(action, {
      focusEditor: true,
      restoreSelection: true,
      ...options,
    });
  }, [runToolbarCommand]);

  const runUtilityToolbarCommand = useCallback((action) => {
    runToolbarCommand(action, {
      focusEditor: false,
      restoreSelection: false,
    });
  }, [runToolbarCommand]);

  const handleToggleBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor]);

  const handleToggleItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor]);

  const handleToggleUnderline = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  }, [editor]);

  const handleToggleStrikethrough = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  }, [editor]);

  const handleToggleCode = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
  }, [editor]);

  const handleToggleHighlight = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'highlight');
  }, [editor]);

  const applyHeading = useCallback((tag) => {
    applyBlockStyle(editor, tag);
  }, [editor]);

  const applyParagraph = useCallback(() => {
    applyBlockStyle(editor, 'paragraph');
  }, [editor]);

  const applyQuote = useCallback(() => {
    applyBlockStyle(editor, 'quote');
  }, [editor]);

  const toggleList = useCallback((listType) => {
    applyBlockStyle(editor, listType);
  }, [editor]);

  const applyAlignment = useCallback((format) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
  }, [editor]);

  const handleOutdent = useCallback(() => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  const handleIndent = useCallback(() => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  const handleUndo = useCallback(() => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  }, [editor]);

  const handleRedo = useCallback(() => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  }, [editor]);

  const handleLink = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }
      const anchor = selection.anchor.getNode();
      const focus = selection.focus.getNode();
      const targetNode = anchor === focus ? anchor : selection.isBackward() ? focus : anchor;
      const linkNode = $isLinkNode(targetNode) ? targetNode : targetNode.getParent();
      const currentUrl = $isLinkNode(linkNode) ? linkNode.getURL() : '';
      const url = window.prompt(t('reports.review.editor.linkPrompt'), currentUrl || 'https://');
      if (url === null) {
        return;
      }
      if (!url.trim()) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        return;
      }
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url.trim());
    });
  }, [editor, t]);

  const handleInsertImagePlaceholder = useCallback(() => {
    editor.update(() => {
      const placeholder = $createPlaceholderNode();
      placeholder.append($createTextNode(t('reports.review.editor.imagePlaceholder')));
      $insertNodes([placeholder]);
    });
  }, [editor, t]);

  const handleInsertTable = useCallback(() => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: 3, rows: 3 });
  }, [editor]);

  const insertReportVisualNode = useCallback((nodeData, options = {}) => {
    const {
      selectionSnapshot = null,
      fallbackToAppend = true,
      focusEditor = true,
    } = options;
    const payload = cloneSerializable(nodeData) || {};
    if (payload.source && typeof payload.source === 'object' && !payload.source.insertedAt) {
      payload.source = {
        ...payload.source,
        insertedAt: new Date().toISOString(),
      };
    }
    editor.update(() => {
      if (selectionSnapshot) {
        restoreRangeSelectionSnapshot(selectionSnapshot);
      }
      const visualNode = $createReportVisualNode(payload);
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $insertNodes([visualNode]);
        return;
      }
      if (fallbackToAppend) {
        $getRoot().append(visualNode);
      }
    });
    if (focusEditor) {
      editor.focus();
    }
  }, [editor]);

  const openVisualPicker = useCallback((mode) => {
    let snapshot = latestRangeSelectionSnapshotRef.current;
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      const liveSnapshot = buildRangeSelectionSnapshot(selection);
      if (liveSnapshot) {
        snapshot = liveSnapshot;
      }
    });
    visualPickerSelectionSnapshotRef.current = snapshot ? { ...snapshot } : null;
    isVisualDragSessionActiveRef.current = false;
    didVisualDragDropSucceedRef.current = false;
    activeDraggedVisualItemRef.current = null;
    if (visualDragBridgeRef?.current) {
      visualDragBridgeRef.current.didDropSucceed = false;
      visualDragBridgeRef.current.lastClearReason = null;
    }
    setIsVisualPickerTemporarilyHidden(false);
    setVisualPickerMode(mode === 'map' ? 'map' : 'chart');
    setIsVisualPickerOpen(true);
  }, [editor, visualDragBridgeRef]);

  const insertBlankChart = useCallback(() => {
    insertVisualBlock(editor, 'chart', t);
  }, [editor, t]);

  const insertBlankMap = useCallback(() => {
    insertVisualBlock(editor, 'map', t);
  }, [editor, t]);

  const handleInsertChart = useCallback(() => {
    openVisualPicker('chart');
  }, [openVisualPicker]);

  const handleInsertMap = useCallback(() => {
    openVisualPicker('map');
  }, [openVisualPicker]);

  const handleInsertPlaceholder = useCallback(() => {
    insertManualPlaceholder(editor, t('reports.review.editor.insert.placeholderDefault'));
  }, [editor, t]);

  const handleInsertVisualPickerItemAtCursor = useCallback((item) => {
    if (!item || item.disabled) {
      return;
    }
    const nodeData = item?.dragPayload?.nodeData || null;
    if (!nodeData) {
      if (typeof item.onSelect === 'function') {
        item.onSelect();
      }
      return;
    }
    insertReportVisualNode(nodeData, {
      selectionSnapshot: visualPickerSelectionSnapshotRef.current,
      fallbackToAppend: true,
      focusEditor: true,
    });
  }, [insertReportVisualNode]);

  const cancelPendingVisualPickerDragHide = useCallback(() => {
    if (visualPickerDragHideRafRef.current == null || typeof window === 'undefined') {
      return;
    }
    window.cancelAnimationFrame(visualPickerDragHideRafRef.current);
    visualPickerDragHideRafRef.current = null;
  }, []);

  const resetVisualDragSession = useCallback(() => {
    cancelPendingVisualPickerDragHide();
    isVisualDragSessionActiveRef.current = false;
    didVisualDragDropSucceedRef.current = false;
    activeDraggedVisualItemRef.current = null;
    if (visualDragBridgeRef?.current) {
      visualDragBridgeRef.current.didDropSucceed = false;
      visualDragBridgeRef.current.lastClearReason = null;
    }
  }, [cancelPendingVisualPickerDragHide, visualDragBridgeRef]);

  const handleVisualPickerOpenChange = useCallback((nextOpen) => {
    setIsVisualPickerOpen(nextOpen);
    if (!nextOpen) {
      setIsVisualPickerTemporarilyHidden(false);
      resetVisualDragSession();
    }
  }, [resetVisualDragSession]);

  const handleVisualPickerDragStartItem = useCallback((item) => {
    if (!isVisualPickerOpen || visualPickerMode !== 'chart') {
      return;
    }
    cancelPendingVisualPickerDragHide();
    isVisualDragSessionActiveRef.current = true;
    didVisualDragDropSucceedRef.current = false;
    activeDraggedVisualItemRef.current = item || null;
    if (visualDragBridgeRef?.current) {
      visualDragBridgeRef.current.didDropSucceed = false;
      visualDragBridgeRef.current.lastClearReason = null;
    }
    if (typeof window === 'undefined') {
      setIsVisualPickerTemporarilyHidden(true);
      return;
    }
    visualPickerDragHideRafRef.current = window.requestAnimationFrame(() => {
      visualPickerDragHideRafRef.current = null;
      const didDropSucceed = Boolean(
        didVisualDragDropSucceedRef.current
        || visualDragBridgeRef?.current?.didDropSucceed
      );
      if (!isVisualDragSessionActiveRef.current || didDropSucceed) {
        return;
      }
      setIsVisualPickerTemporarilyHidden(true);
    });
  }, [
    cancelPendingVisualPickerDragHide,
    isVisualPickerOpen,
    visualPickerMode,
    visualDragBridgeRef,
  ]);

  const handleVisualPickerDragEndItem = useCallback(() => {
    if (!isVisualDragSessionActiveRef.current) {
      return;
    }
    cancelPendingVisualPickerDragHide();
    const didDropSucceed = Boolean(
      didVisualDragDropSucceedRef.current
      || visualDragBridgeRef?.current?.didDropSucceed
    );
    setIsVisualPickerTemporarilyHidden(false);
    if (didDropSucceed) {
      setIsVisualPickerOpen(false);
    }
    resetVisualDragSession();
  }, [cancelPendingVisualPickerDragHide, resetVisualDragSession, visualDragBridgeRef]);

  useEffect(() => {
    if (!visualDragBridgeRef?.current) {
      return;
    }
    if (visualDragBridgeRef.current.didDropSucceed) {
      didVisualDragDropSucceedRef.current = true;
    }
  });

  useEffect(() => () => {
    cancelPendingVisualPickerDragHide();
  }, [cancelPendingVisualPickerDragHide]);

  const jumpToHeading = useCallback((entry) => {
    if (!entry) {
      return;
    }
    const byKey = editor.getElementByKey(entry.key);
    const byId = entry.anchorId && typeof document !== 'undefined'
      ? document.getElementById(entry.anchorId)
      : null;
    const element = byKey || byId;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [editor]);

  useEffect(() => {
    if (advancedEditorEnabled) {
      return;
    }
    setIsPaletteOpen(false);
    setIsFindReplaceOpen(false);
  }, [advancedEditorEnabled]);

  useEffect(() => {
    const measureToolbarCompaction = () => {
      const leftEl = toolbarLeftRef.current;
      const toolbarWidth = Math.round(toolbarBarRef.current?.getBoundingClientRect().width || 0);
      setToolbarWidth((current) => (current === toolbarWidth ? current : toolbarWidth));
      if (!leftEl) {
        return;
      }
      const children = Array.from(leftEl.children).filter((node) => node instanceof HTMLElement);
      if (children.length <= 1) {
        setIsCompactToolbar(false);
        return;
      }
      const firstTop = children[0].offsetTop;
      const wrapped = children.some((child) => Math.abs(child.offsetTop - firstTop) > 1);
      setIsCompactToolbar((current) => {
        if (wrapped) {
          if (toolbarWidth > 0) {
            toolbarCompactBreakWidthRef.current = Math.max(toolbarCompactBreakWidthRef.current, toolbarWidth);
          }
          return current ? current : true;
        }
        if (!current) {
          toolbarCompactBreakWidthRef.current = 0;
          return false;
        }
        const exitThreshold = toolbarCompactBreakWidthRef.current > 0
          ? toolbarCompactBreakWidthRef.current + 120
          : 0;
        if (exitThreshold > 0 && toolbarWidth > 0 && toolbarWidth < exitThreshold) {
          return true;
        }
        toolbarCompactBreakWidthRef.current = 0;
        return false;
      });
    };

    const scheduleMeasure = () => {
      if (toolbarMeasureRafRef.current) {
        cancelAnimationFrame(toolbarMeasureRafRef.current);
      }
      toolbarMeasureRafRef.current = requestAnimationFrame(() => {
        toolbarMeasureRafRef.current = null;
        measureToolbarCompaction();
      });
    };

    if (!toolbarBarRef.current || typeof ResizeObserver === 'undefined') {
      scheduleMeasure();
      return () => {
        if (toolbarMeasureRafRef.current) {
          cancelAnimationFrame(toolbarMeasureRafRef.current);
        }
      };
    }

    const observer = new ResizeObserver(() => {
      const width = Math.round(toolbarBarRef.current?.getBoundingClientRect().width || 0);
      if (width <= 0 || width === toolbarLastMeasuredWidthRef.current) {
        return;
      }
      toolbarLastMeasuredWidthRef.current = width;
      scheduleMeasure();
    });

    observer.observe(toolbarBarRef.current);
    if (toolbarLeftRef.current) {
      observer.observe(toolbarLeftRef.current);
    }
    scheduleMeasure();

    return () => {
      if (toolbarMeasureRafRef.current) {
        cancelAnimationFrame(toolbarMeasureRafRef.current);
      }
      observer.disconnect();
    };
  }, [advancedEditorEnabled]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const selectionSnapshot = buildRangeSelectionSnapshot(selection);
          if (selectionSnapshot) {
            latestRangeSelectionSnapshotRef.current = selectionSnapshot;
            if (isVisualPickerOpen) {
              visualPickerSelectionSnapshotRef.current = selectionSnapshot;
            }
          }
          setIsBold(selection.hasFormat('bold'));
          setIsItalic(selection.hasFormat('italic'));
          setIsUnderline(selection.hasFormat('underline'));
          setIsStrikethrough(selection.hasFormat('strikethrough'));
          setIsCode(selection.hasFormat('code'));
          setIsHighlight(selection.hasFormat('highlight'));
          const currentBlockType = resolveSelectionBlockType(selection);
          setBlockType(currentBlockType);
          const topLevel = getSelectionTopLevelElement(selection);
          const formatType = topLevel && typeof topLevel.getFormatType === 'function'
            ? topLevel.getFormatType()
            : 'left';
          setAlignment(formatType || 'left');
        }
        const nextHeadingEntries = collectHeadingEntries();
        setHeadingEntries((current) => (
          areHeadingEntriesEqual(nextHeadingEntries, current)
            ? current
            : nextHeadingEntries
        ));
      });
    });
  }, [editor, isVisualPickerOpen]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event) => {
        const { key, metaKey, ctrlKey, altKey, shiftKey } = event;
        const normalizedKey = key.length === 1 ? key.toLowerCase() : key;
        const isModifier = metaKey || ctrlKey;

        if (normalizedKey === 'Tab') {
          let isListSelection = false;
          editor.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
              return;
            }
            const topLevel = getSelectionTopLevelElement(selection);
            if (!topLevel) {
              return;
            }
            isListSelection = $isListNode(topLevel) || topLevel.getType?.() === 'listitem';
          });
          if (isListSelection) {
            event.preventDefault();
            if (shiftKey) {
              runMutatingToolbarCommand(handleOutdent, { restoreSelection: false });
            } else {
              runMutatingToolbarCommand(handleIndent, { restoreSelection: false });
            }
            return true;
          }
          return false;
        }

        if (!isModifier) {
          return false;
        }

        if (advancedEditorEnabled && normalizedKey === 'k' && !shiftKey) {
          event.preventDefault();
          setIsPaletteOpen((current) => (current ? current : true));
          return true;
        }

        if (advancedEditorEnabled && normalizedKey === 'f' && !shiftKey) {
          event.preventDefault();
          setIsFindReplaceOpen(true);
          return true;
        }

        if (normalizedKey === 'z') {
          event.preventDefault();
          if (shiftKey) {
            runMutatingToolbarCommand(handleRedo, { restoreSelection: false });
          } else {
            runMutatingToolbarCommand(handleUndo, { restoreSelection: false });
          }
          return true;
        }

        if (normalizedKey === 'y' && !shiftKey) {
          event.preventDefault();
          runMutatingToolbarCommand(handleRedo, { restoreSelection: false });
          return true;
        }

        if (shiftKey && normalizedKey === 'i') {
          event.preventDefault();
          runMutatingToolbarCommand(handleInsertImagePlaceholder);
          return true;
        }

        if (altKey) {
          if (normalizedKey === '1') {
            event.preventDefault();
            runMutatingToolbarCommand(() => applyHeading('h1'));
            return true;
          }
          if (normalizedKey === '2') {
            event.preventDefault();
            runMutatingToolbarCommand(() => applyHeading('h2'));
            return true;
          }
          if (normalizedKey === '3') {
            event.preventDefault();
            runMutatingToolbarCommand(() => applyHeading('h3'));
            return true;
          }
          if (normalizedKey === '0') {
            event.preventDefault();
            runMutatingToolbarCommand(applyParagraph);
            return true;
          }
          if (normalizedKey === 'q') {
            event.preventDefault();
            runMutatingToolbarCommand(applyQuote);
            return true;
          }
          if (normalizedKey === 't') {
            event.preventDefault();
            runMutatingToolbarCommand(handleInsertTable);
            return true;
          }
          return false;
        }

        switch (normalizedKey) {
          case 'b':
            event.preventDefault();
            runMutatingToolbarCommand(handleToggleBold, { restoreSelection: false });
            return true;
          case 'i':
            if (!shiftKey) {
              event.preventDefault();
              runMutatingToolbarCommand(handleToggleItalic, { restoreSelection: false });
              return true;
            }
            return false;
          case 'u':
            event.preventDefault();
            runMutatingToolbarCommand(handleToggleUnderline, { restoreSelection: false });
            return true;
          case 'x':
            if (shiftKey) {
              event.preventDefault();
              runMutatingToolbarCommand(handleToggleStrikethrough, { restoreSelection: false });
              return true;
            }
            return false;
          case 'c':
            if (shiftKey) {
              event.preventDefault();
              runMutatingToolbarCommand(handleToggleCode, { restoreSelection: false });
              return true;
            }
            return false;
          case 'h':
            if (shiftKey) {
              event.preventDefault();
              runMutatingToolbarCommand(handleToggleHighlight, { restoreSelection: false });
              return true;
            }
            return false;
          case 'l':
            event.preventDefault();
            runMutatingToolbarCommand(() => applyAlignment('left'), { restoreSelection: false });
            return true;
          case 'e':
            event.preventDefault();
            runMutatingToolbarCommand(() => applyAlignment('center'), { restoreSelection: false });
            return true;
          case 'r':
            event.preventDefault();
            runMutatingToolbarCommand(() => applyAlignment('right'), { restoreSelection: false });
            return true;
          case 'j':
            event.preventDefault();
            runMutatingToolbarCommand(() => applyAlignment('justify'), { restoreSelection: false });
            return true;
          case '7':
            if (shiftKey) {
              event.preventDefault();
              runMutatingToolbarCommand(() => toggleList('number'), { restoreSelection: false });
              return true;
            }
            return false;
          case '8':
            if (shiftKey) {
              event.preventDefault();
              runMutatingToolbarCommand(() => toggleList('bullet'), { restoreSelection: false });
              return true;
            }
            return false;
          case 'k':
            if (shiftKey) {
              event.preventDefault();
              runMutatingToolbarCommand(handleLink, { restoreSelection: false });
              return true;
            }
            return false;
          default:
            return false;
        }
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [
    advancedEditorEnabled,
    editor,
    applyAlignment,
    applyHeading,
    applyParagraph,
    applyQuote,
    handleIndent,
    handleInsertImagePlaceholder,
    handleInsertTable,
    handleLink,
    handleOutdent,
    handleRedo,
    handleToggleBold,
    handleToggleCode,
    handleToggleHighlight,
    handleToggleItalic,
    handleToggleStrikethrough,
    handleToggleUnderline,
    handleUndo,
    runMutatingToolbarCommand,
    toggleList,
  ]);

  const statusLabel = useMemo(() => {
    let displayStatus = status;
    if (!REPORT_DRAFT_LOCAL_CACHE_ENABLED) {
      if (status === 'saving_local' || status === 'saved_local_syncing_remote') {
        displayStatus = 'saving';
      } else if (status === 'sync_error' || status === 'saved_local_offline') {
        displayStatus = 'error';
      }
    }
    const key = `reports.review.editor.status.${displayStatus}`;
    const translated = t(key);
    return translated === key ? displayStatus : translated;
  }, [status, t]);
  const statusDetails = useMemo(() => {
    if (status === 'error') {
      return lastErrorMessage || t('reports.review.editor.status.errorDetails');
    }
    if (status === 'sync_error') {
      return lastErrorMessage || (
        REPORT_DRAFT_LOCAL_CACHE_ENABLED
          ? t('reports.review.editor.status.syncError')
          : t('reports.review.editor.status.syncErrorRemoteOnly', {
            defaultValue: 'Background sync failed. Keep this tab open until connection is restored.',
          })
      );
    }
    if (status === 'conflict') {
      return lastErrorMessage || t('reports.review.editor.status.conflict');
    }
    if (status === 'saved_local_offline') {
      return t('reports.review.editor.status.offlineRetry');
    }
    if (
      (status === 'saving_local' || status === 'saved_local_syncing_remote')
      && isSlowSaving
    ) {
      return t('reports.review.editor.status.savingLargeDraft', {
        defaultValue: 'Saving large draft...',
      });
    }
    if ((status === 'saved' || status === 'saved_local_syncing_remote' || status === 'saved_local_offline')
      && lastSavedAt instanceof Date) {
      return new Intl.DateTimeFormat(locale || undefined, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(lastSavedAt);
    }
    return '';
  }, [isSlowSaving, lastErrorMessage, lastSavedAt, locale, status, t]);

  const setFindPanelOpen = useCallback((open) => {
    if (!advancedEditorEnabled) {
      return;
    }
    setIsFindReplaceOpen(open);
    if (!open) {
      setFindState((prev) => ({ ...prev, replacedCount: 0 }));
    }
  }, [advancedEditorEnabled]);

  const setPaletteOpen = useCallback((open) => {
    if (!advancedEditorEnabled) {
      setIsPaletteOpen(false);
      return;
    }
    const normalizedOpen = typeof open === 'boolean' ? open : false;
    setIsPaletteOpen((current) => (current === normalizedOpen ? current : normalizedOpen));
  }, [advancedEditorEnabled]);

  const handleToggleFindReplacePanel = useCallback(() => {
    runUtilityToolbarCommand(() => setFindPanelOpen(!isFindReplaceOpen));
  }, [isFindReplaceOpen, runUtilityToolbarCommand, setFindPanelOpen]);

  const handleOpenFindReplacePanel = useCallback(() => {
    runUtilityToolbarCommand(() => setFindPanelOpen(true));
  }, [runUtilityToolbarCommand, setFindPanelOpen]);

  const handleOpenCommandPalette = useCallback(() => {
    runUtilityToolbarCommand(() => setPaletteOpen(true));
  }, [runUtilityToolbarCommand, setPaletteOpen]);

  const handleFindStateChange = useCallback((state) => {
    setFindState((current) => {
      const next = { ...current, ...state };
      if (
        next.matchCount === current.matchCount
        && next.currentMatch === current.currentMatch
        && next.replacedCount === current.replacedCount
      ) {
        return current;
      }
      return next;
    });
  }, []);

  const styleMenuItems = useMemo(() => ([
    {
      id: 'paragraph',
      label: labels.paragraph,
      shortcut: shortcuts.paragraph,
      isActive: blockType === 'paragraph',
      onSelect: () => runMutatingToolbarCommand(applyParagraph),
    },
    {
      id: 'h1',
      label: labels.heading1,
      shortcut: shortcuts.heading1,
      isActive: blockType === 'h1',
      onSelect: () => runMutatingToolbarCommand(() => applyHeading('h1')),
    },
    {
      id: 'h2',
      label: labels.heading2,
      shortcut: shortcuts.heading2,
      isActive: blockType === 'h2',
      onSelect: () => runMutatingToolbarCommand(() => applyHeading('h2')),
    },
    {
      id: 'h3',
      label: labels.heading3,
      shortcut: shortcuts.heading3,
      isActive: blockType === 'h3',
      onSelect: () => runMutatingToolbarCommand(() => applyHeading('h3')),
    },
    {
      id: 'quote',
      label: labels.quote,
      shortcut: shortcuts.quote,
      isActive: blockType === 'quote',
      onSelect: () => runMutatingToolbarCommand(applyQuote),
    },
    {
      id: 'bullet',
      label: labels.bullets,
      shortcut: shortcuts.bullets,
      isActive: blockType === 'bullet',
      onSelect: () => runMutatingToolbarCommand(() => toggleList('bullet')),
    },
    {
      id: 'number',
      label: labels.numbered,
      shortcut: shortcuts.numbered,
      isActive: blockType === 'number',
      onSelect: () => runMutatingToolbarCommand(() => toggleList('number')),
    },
  ]), [
    applyHeading,
    applyParagraph,
    applyQuote,
    blockType,
    labels,
    runMutatingToolbarCommand,
    shortcuts,
    toggleList,
  ]);

  const templateVisualInsertItems = useMemo(
    () => collectTemplateVisualInsertItems(templateDocument, t),
    [templateDocument, t]
  );

  const analyticsVisualPresetItems = useMemo(
    () => buildAnalyticsVisualPresetItems({
      hourlyData,
      dailyData,
      selectedFarm,
      selectedSections,
      timeRange,
      t,
    }),
    [dailyData, hourlyData, selectedFarm, selectedSections, timeRange, t]
  );

  const currentCoverageMapItem = useMemo(
    () => buildCurrentCoverageMapVisualItem({ selectedFarm, t }),
    [selectedFarm, t]
  );

  const visualPickerLabels = useMemo(() => ({
    chartTitle: t('reports.review.editor.insert.visualPicker.chartTitle', { defaultValue: 'Insert chart block' }),
    mapTitle: t('reports.review.editor.insert.visualPicker.mapTitle', { defaultValue: 'Insert map block' }),
    chartDescription: t('reports.review.editor.insert.visualPicker.chartDescription', { defaultValue: 'Choose a chart template, module visual, or analytics graph snapshot to insert.' }),
    mapDescription: t('reports.review.editor.insert.visualPicker.mapDescription', { defaultValue: 'Choose a map template or serialized coverage map to insert.' }),
    searchChart: t('reports.review.editor.insert.visualPicker.searchChartPlaceholder', { defaultValue: 'Search charts...' }),
    searchMap: t('reports.review.editor.insert.visualPicker.searchMapPlaceholder', { defaultValue: 'Search maps...' }),
    empty: t('reports.review.editor.insert.visualPicker.empty', { defaultValue: 'No visuals found.' }),
    templates: t('reports.review.editor.insert.visualPicker.sections.templates', { defaultValue: 'Templates' }),
    moduleVisuals: t('reports.review.editor.insert.visualPicker.sections.moduleVisuals', { defaultValue: 'Module visuals' }),
    analytics: t('reports.review.editor.insert.visualPicker.sections.analytics', { defaultValue: 'Analytics graphs' }),
    coverage: t('reports.review.editor.insert.visualPicker.sections.coverage', { defaultValue: 'Coverage' }),
    blankChartLabel: t('reports.review.editor.insert.visualPicker.templates.blankChartLabel', { defaultValue: 'Blank chart' }),
    blankChartDescription: t('reports.review.editor.insert.visualPicker.templates.blankChartDescription', { defaultValue: 'Insert an empty editable chart block.' }),
    blankMapLabel: t('reports.review.editor.insert.visualPicker.templates.blankMapLabel', { defaultValue: 'Blank map' }),
    blankMapDescription: t('reports.review.editor.insert.visualPicker.templates.blankMapDescription', { defaultValue: 'Insert an empty editable map block.' }),
    insertAtCursor: t('reports.review.editor.insert.visualPicker.insertAtCursor', { defaultValue: 'Insert at cursor' }),
    dragToPlaceHint: t('reports.review.editor.insert.visualPicker.dragToPlaceHint', { defaultValue: 'Drag into document to place precisely, or insert at cursor.' }),
  }), [t]);

  const visualPickerSections = useMemo(() => {
    const buildDragPayload = (descriptor) => (
      descriptor?.disabled || !descriptor?.nodeData
        ? null
        : {
          kind: 'visualInsertItem',
          itemId: descriptor.id,
          visualType: descriptor.visualType,
          sourceKind: descriptor.sourceKind,
          nodeData: cloneSerializable(descriptor.nodeData),
        }
    );

    const buildPreview = (descriptor) => ({
      visualType: descriptor.visualType,
      chart: descriptor.nodeData?.chart || null,
      fallbackCoverage: descriptor.nodeData?.fallbackCoverage || null,
      title: descriptor.label,
    });

    const descriptorToItem = (descriptor) => {
      const isMap = descriptor.visualType === 'map';
      const icon = isMap ? MapIcon : BarChart3;
      return {
        id: descriptor.id,
        label: descriptor.label,
        description: descriptor.description,
        icon,
        preview: buildPreview(descriptor),
        disabled: Boolean(descriptor.disabled),
        disabledReason: descriptor.disabledReason,
        dragPayload: buildDragPayload(descriptor),
        onSelect: descriptor.disabled ? undefined : () => insertReportVisualNode(descriptor.nodeData),
      };
    };

    if (visualPickerMode === 'map') {
      const templateMapItems = templateVisualInsertItems
        .filter(item => item.visualType === 'map')
        .map(descriptorToItem);
      const coverageItems = currentCoverageMapItem ? [descriptorToItem(currentCoverageMapItem)] : [];

      return [
        {
          id: 'visual-picker-templates',
          label: visualPickerLabels.templates,
          items: [(() => {
            const blankMapNodeData = buildBlankVisualNodeData({ visualType: 'map', t });
            return {
              id: 'visual-picker-template-blank-map',
              label: visualPickerLabels.blankMapLabel,
              description: visualPickerLabels.blankMapDescription,
              icon: MapIcon,
              preview: { visualType: 'map', blank: true, title: visualPickerLabels.blankMapLabel },
              dragPayload: {
                kind: 'visualInsertItem',
                itemId: 'visual-picker-template-blank-map',
                visualType: 'map',
                sourceKind: 'systemPreset',
                nodeData: cloneSerializable(blankMapNodeData),
              },
              onSelect: insertBlankMap,
            };
          })()],
        },
        {
          id: 'visual-picker-module-maps',
          label: visualPickerLabels.moduleVisuals,
          items: templateMapItems,
        },
        {
          id: 'visual-picker-coverage',
          label: visualPickerLabels.coverage,
          items: coverageItems,
        },
      ].filter(section => section.items.length > 0);
    }

    const templateChartItems = templateVisualInsertItems
      .filter(item => item.visualType === 'chart')
      .map(descriptorToItem);
    const analyticsItems = analyticsVisualPresetItems
      .filter(item => item.visualType === 'chart')
      .map(descriptorToItem);

    return [
      {
        id: 'visual-picker-templates',
        label: visualPickerLabels.templates,
        items: [(() => {
          const blankChartNodeData = buildBlankVisualNodeData({ visualType: 'chart', t });
          return {
            id: 'visual-picker-template-blank-chart',
            label: visualPickerLabels.blankChartLabel,
            description: visualPickerLabels.blankChartDescription,
            icon: BarChart3,
            preview: { visualType: 'chart', blank: true, title: visualPickerLabels.blankChartLabel },
            dragPayload: {
              kind: 'visualInsertItem',
              itemId: 'visual-picker-template-blank-chart',
              visualType: 'chart',
              sourceKind: 'systemPreset',
              nodeData: cloneSerializable(blankChartNodeData),
            },
            onSelect: insertBlankChart,
          };
        })()],
      },
      {
        id: 'visual-picker-module-charts',
        label: visualPickerLabels.moduleVisuals,
        items: templateChartItems,
      },
      {
        id: 'visual-picker-analytics',
        label: visualPickerLabels.analytics,
        items: analyticsItems,
      },
    ].filter(section => section.items.length > 0);
  }, [
    analyticsVisualPresetItems,
    t,
    currentCoverageMapItem,
    insertBlankChart,
    insertBlankMap,
    insertReportVisualNode,
    templateVisualInsertItems,
    visualPickerLabels,
    visualPickerMode,
  ]);

  const visualPickerTitle = visualPickerMode === 'map'
    ? visualPickerLabels.mapTitle
    : visualPickerLabels.chartTitle;
  const visualPickerDescription = visualPickerMode === 'map'
    ? visualPickerLabels.mapDescription
    : visualPickerLabels.chartDescription;
  const visualPickerSearchPlaceholder = visualPickerMode === 'map'
    ? visualPickerLabels.searchMap
    : visualPickerLabels.searchChart;

  const insertMenuSections = useMemo(() => ([
    {
      id: 'insert-main',
      label: '',
      items: [
        { id: 'table', label: labels.table, shortcut: shortcuts.table, onSelect: () => runMutatingToolbarCommand(handleInsertTable) },
        { id: 'link', label: labels.link, shortcut: shortcuts.link, onSelect: () => runMutatingToolbarCommand(handleLink) },
        { id: 'image', label: labels.image, shortcut: shortcuts.image, onSelect: () => runMutatingToolbarCommand(handleInsertImagePlaceholder) },
      ],
    },
    {
      id: 'insert-visuals',
      label: t('reports.review.editor.insert.visuals'),
      items: [
        { id: 'chart', label: t('reports.review.editor.insert.chart'), shortcut: '/chart', onSelect: () => runMutatingToolbarCommand(handleInsertChart) },
        { id: 'map', label: t('reports.review.editor.insert.map'), shortcut: '/map', onSelect: () => runMutatingToolbarCommand(handleInsertMap) },
        { id: 'placeholder', label: t('reports.review.editor.insert.placeholder'), shortcut: '/p', onSelect: () => runMutatingToolbarCommand(handleInsertPlaceholder) },
      ],
    },
  ]), [
    handleInsertChart,
    handleInsertImagePlaceholder,
    handleInsertMap,
    handleInsertPlaceholder,
    handleInsertTable,
    handleLink,
    labels.image,
    labels.link,
    labels.table,
    runMutatingToolbarCommand,
    shortcuts.image,
    shortcuts.link,
    shortcuts.table,
    t,
  ]);

  const actionRegistry = useMemo(() => ({
    paragraph: { icon: Pilcrow, label: labels.paragraph, shortcut: shortcuts.paragraph, active: blockType === 'paragraph', onSelect: () => runMutatingToolbarCommand(applyParagraph) },
    heading1: { icon: Heading1, label: labels.heading1, shortcut: shortcuts.heading1, active: blockType === 'h1', onSelect: () => runMutatingToolbarCommand(() => applyHeading('h1')) },
    heading2: { icon: Heading2, label: labels.heading2, shortcut: shortcuts.heading2, active: blockType === 'h2', onSelect: () => runMutatingToolbarCommand(() => applyHeading('h2')) },
    heading3: { icon: Heading3, label: labels.heading3, shortcut: shortcuts.heading3, active: blockType === 'h3', onSelect: () => runMutatingToolbarCommand(() => applyHeading('h3')) },
    quote: { icon: Quote, label: labels.quote, shortcut: shortcuts.quote, active: blockType === 'quote', onSelect: () => runMutatingToolbarCommand(applyQuote) },
    bullets: { icon: List, label: labels.bullets, shortcut: shortcuts.bullets, active: blockType === 'bullet', onSelect: () => runMutatingToolbarCommand(() => toggleList('bullet')) },
    numbered: { icon: ListOrdered, label: labels.numbered, shortcut: shortcuts.numbered, active: blockType === 'number', onSelect: () => runMutatingToolbarCommand(() => toggleList('number')) },
    bold: { icon: Bold, label: labels.bold, shortcut: shortcuts.bold, active: isBold, onSelect: () => runMutatingToolbarCommand(handleToggleBold) },
    italic: { icon: Italic, label: labels.italic, shortcut: shortcuts.italic, active: isItalic, onSelect: () => runMutatingToolbarCommand(handleToggleItalic) },
    underline: { icon: Underline, label: labels.underline, shortcut: shortcuts.underline, active: isUnderline, onSelect: () => runMutatingToolbarCommand(handleToggleUnderline) },
    strikethrough: { icon: Strikethrough, label: labels.strikethrough, shortcut: shortcuts.strikethrough, active: isStrikethrough, onSelect: () => runMutatingToolbarCommand(handleToggleStrikethrough) },
    code: { icon: Code, label: labels.code, shortcut: shortcuts.code, active: isCode, onSelect: () => runMutatingToolbarCommand(handleToggleCode) },
    highlight: { icon: Highlighter, label: labels.highlight, shortcut: shortcuts.highlight, active: isHighlight, onSelect: () => runMutatingToolbarCommand(handleToggleHighlight) },
    alignLeft: { icon: AlignLeft, label: labels.alignLeft, shortcut: shortcuts.alignLeft, active: alignment === 'left', onSelect: () => runMutatingToolbarCommand(() => applyAlignment('left')) },
    alignCenter: { icon: AlignCenter, label: labels.alignCenter, shortcut: shortcuts.alignCenter, active: alignment === 'center', onSelect: () => runMutatingToolbarCommand(() => applyAlignment('center')) },
    alignRight: { icon: AlignRight, label: labels.alignRight, shortcut: shortcuts.alignRight, active: alignment === 'right', onSelect: () => runMutatingToolbarCommand(() => applyAlignment('right')) },
    alignJustify: { icon: AlignJustify, label: labels.alignJustify, shortcut: shortcuts.alignJustify, active: alignment === 'justify', onSelect: () => runMutatingToolbarCommand(() => applyAlignment('justify')) },
    outdent: { icon: IndentDecrease, label: labels.outdent, shortcut: shortcuts.outdent, active: false, onSelect: () => runMutatingToolbarCommand(handleOutdent) },
    indent: { icon: IndentIncrease, label: labels.indent, shortcut: shortcuts.indent, active: false, onSelect: () => runMutatingToolbarCommand(handleIndent) },
    table: { icon: Table2, label: labels.table, shortcut: shortcuts.table, active: false, onSelect: () => runMutatingToolbarCommand(handleInsertTable) },
    link: { icon: Link2, label: labels.link, shortcut: shortcuts.link, active: false, onSelect: () => runMutatingToolbarCommand(handleLink) },
    image: { icon: Image, label: labels.image, shortcut: shortcuts.image, active: false, onSelect: () => runMutatingToolbarCommand(handleInsertImagePlaceholder) },
  }), [
    alignment,
    applyAlignment,
    applyHeading,
    applyParagraph,
    applyQuote,
    blockType,
    handleIndent,
    handleInsertImagePlaceholder,
    handleInsertTable,
    handleLink,
    handleOutdent,
    handleToggleBold,
    handleToggleCode,
    handleToggleHighlight,
    handleToggleItalic,
    handleToggleStrikethrough,
    handleToggleUnderline,
    isBold,
    isCode,
    isHighlight,
    isItalic,
    isStrikethrough,
    isUnderline,
    labels,
    runMutatingToolbarCommand,
    shortcuts,
    toggleList,
  ]);

  const renderActionButton = useCallback((actionKey) => {
    const action = actionRegistry[actionKey];
    if (!action) {
      return null;
    }
    const Icon = action.icon;
    return (
      <button
        key={actionKey}
        type="button"
        className={`report-draft-editor__toolbar-button${action.active ? ' is-active' : ''}`}
        aria-label={action.label}
        title={getTooltip(action.label, action.shortcut)}
        aria-pressed={action.active}
        onMouseDown={preventToolbarMouseDown}
        onClick={action.onSelect}
      >
        <Icon size={18} />
      </button>
    );
  }, [actionRegistry, getTooltip, preventToolbarMouseDown]);

  const toolbarPresentationMode = isCompactToolbar
    ? 'compact'
    : (toolbarWidth >= 1280 ? 'spacious' : 'comfortable');
  const isSpaciousToolbar = toolbarPresentationMode === 'spacious';
  const utilityLabels = useMemo(() => ({
    find: t('reports.review.editor.toolbar.findShort', { defaultValue: 'Find' }),
    command: t('reports.review.editor.toolbar.commandPaletteShort', { defaultValue: 'Command' }),
  }), [t]);

  const primaryInlineKeys = toolbarPresentationMode === 'compact'
    ? ['bold', 'italic', 'underline']
    : toolbarPresentationMode === 'spacious'
      ? ['bold', 'italic', 'underline', 'highlight', 'strikethrough', 'code']
      : ['bold', 'italic', 'underline', 'highlight', 'strikethrough'];
  const primaryAlignmentKeys = toolbarPresentationMode === 'compact'
    ? ['alignLeft']
    : toolbarPresentationMode === 'spacious'
      ? ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify', 'outdent', 'indent']
      : ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'];
  const primaryListKeys = LIST_INSERT_ACTIONS;
  const overflowMenuSections = useMemo(() => {
    let sections = [];

    if (toolbarPresentationMode === 'compact') {
      sections = [
        { id: 'overflow-format', label: t('reports.review.editor.commandPalette.groups.formatting'), items: ['strikethrough', 'code', 'highlight'] },
        { id: 'overflow-layout', label: t('reports.review.editor.commandPalette.groups.editor'), items: ['alignCenter', 'alignRight', 'alignJustify', 'outdent', 'indent'] },
      ];
    } else if (toolbarPresentationMode === 'comfortable') {
      sections = [
        { id: 'overflow-format', label: t('reports.review.editor.commandPalette.groups.formatting'), items: ['code'] },
        { id: 'overflow-layout', label: t('reports.review.editor.commandPalette.groups.editor'), items: ['outdent', 'indent'] },
      ];
    }

    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((actionKey) => actionRegistry[actionKey]),
      }))
      .filter((section) => section.items.length > 0);
  }, [actionRegistry, t, toolbarPresentationMode]);
  const showOverflowMenu = overflowMenuSections.length > 0;

  const commandPaletteSections = useMemo(() => ([
    {
      id: 'formatting',
      label: t('reports.review.editor.commandPalette.groups.formatting'),
      items: [
        ...TEXT_STYLE_ACTIONS.map((style) => {
          if (style === 'paragraph') {
            return { id: 'cp-paragraph', label: labels.paragraph, shortcut: shortcuts.paragraph, icon: Pilcrow, onSelect: () => runMutatingToolbarCommand(applyParagraph) };
          }
          if (style === 'h1') {
            return { id: 'cp-h1', label: labels.heading1, shortcut: shortcuts.heading1, icon: Heading1, onSelect: () => runMutatingToolbarCommand(() => applyHeading('h1')) };
          }
          if (style === 'h2') {
            return { id: 'cp-h2', label: labels.heading2, shortcut: shortcuts.heading2, icon: Heading2, onSelect: () => runMutatingToolbarCommand(() => applyHeading('h2')) };
          }
          if (style === 'h3') {
            return { id: 'cp-h3', label: labels.heading3, shortcut: shortcuts.heading3, icon: Heading3, onSelect: () => runMutatingToolbarCommand(() => applyHeading('h3')) };
          }
          if (style === 'quote') {
            return { id: 'cp-quote', label: labels.quote, shortcut: shortcuts.quote, icon: Quote, onSelect: () => runMutatingToolbarCommand(applyQuote) };
          }
          if (style === 'bullet') {
            return { id: 'cp-bullet', label: labels.bullets, shortcut: shortcuts.bullets, icon: List, onSelect: () => runMutatingToolbarCommand(() => toggleList('bullet')) };
          }
          return { id: 'cp-number', label: labels.numbered, shortcut: shortcuts.numbered, icon: ListOrdered, onSelect: () => runMutatingToolbarCommand(() => toggleList('number')) };
        }),
      ],
    },
    {
      id: 'insert',
      label: t('reports.review.editor.commandPalette.groups.insert'),
      items: [
        { id: 'cp-table', label: labels.table, shortcut: shortcuts.table, icon: Table2, onSelect: () => runMutatingToolbarCommand(handleInsertTable) },
        { id: 'cp-link', label: labels.link, shortcut: shortcuts.link, icon: Link2, onSelect: () => runMutatingToolbarCommand(handleLink) },
        { id: 'cp-image', label: labels.image, shortcut: shortcuts.image, icon: Image, onSelect: () => runMutatingToolbarCommand(handleInsertImagePlaceholder) },
        { id: 'cp-chart', label: t('reports.review.editor.insert.chart'), shortcut: '/chart', icon: BarChart3, onSelect: () => runMutatingToolbarCommand(handleInsertChart) },
        { id: 'cp-map', label: t('reports.review.editor.insert.map'), shortcut: '/map', icon: MapIcon, onSelect: () => runMutatingToolbarCommand(handleInsertMap) },
      ],
    },
    {
      id: 'navigation',
      label: t('reports.review.editor.commandPalette.groups.navigation'),
      items: headingEntries.map((entry) => ({
        id: `cp-heading-${entry.key}`,
        label: `${entry.number} ${entry.label || t('reports.review.editor.commandPalette.untitledHeading')}`,
        shortcut: `H${entry.level}`,
        icon: Heading1,
        onSelect: () => runUtilityToolbarCommand(() => jumpToHeading(entry)),
      })),
    },
    {
      id: 'editor',
      label: t('reports.review.editor.commandPalette.groups.editor'),
      items: [
        { id: 'cp-undo', label: labels.undo, shortcut: shortcuts.undo, icon: Undo2, onSelect: () => runMutatingToolbarCommand(handleUndo, { restoreSelection: false }) },
        { id: 'cp-redo', label: labels.redo, shortcut: shortcuts.redo, icon: Redo2, onSelect: () => runMutatingToolbarCommand(handleRedo, { restoreSelection: false }) },
        { id: 'cp-find', label: labels.findReplace, shortcut: shortcuts.find, icon: Search, onSelect: handleOpenFindReplacePanel },
      ],
    },
  ]), [
    applyHeading,
    applyParagraph,
    applyQuote,
    handleInsertChart,
    handleInsertImagePlaceholder,
    handleInsertMap,
    handleInsertTable,
    handleLink,
    handleOpenFindReplacePanel,
    handleRedo,
    handleUndo,
    headingEntries,
    jumpToHeading,
    labels,
    runMutatingToolbarCommand,
    runUtilityToolbarCommand,
    shortcuts,
    t,
    toggleList,
  ]);

  const slashItems = useMemo(() => ([
    {
      id: '/p',
      label: t('reports.review.editor.slashCommands.items.paragraph'),
      searchText: t('reports.review.editor.slashCommands.search.paragraph'),
      shortcut: shortcuts.paragraph,
      onSelect: applyParagraph,
    },
    {
      id: '/h1',
      label: t('reports.review.editor.slashCommands.items.heading1'),
      searchText: t('reports.review.editor.slashCommands.search.heading'),
      shortcut: shortcuts.heading1,
      onSelect: () => applyHeading('h1'),
    },
    {
      id: '/h2',
      label: t('reports.review.editor.slashCommands.items.heading2'),
      searchText: t('reports.review.editor.slashCommands.search.heading'),
      shortcut: shortcuts.heading2,
      onSelect: () => applyHeading('h2'),
    },
    {
      id: '/h3',
      label: t('reports.review.editor.slashCommands.items.heading3'),
      searchText: t('reports.review.editor.slashCommands.search.heading'),
      shortcut: shortcuts.heading3,
      onSelect: () => applyHeading('h3'),
    },
    {
      id: '/quote',
      label: t('reports.review.editor.slashCommands.items.quote'),
      searchText: t('reports.review.editor.slashCommands.search.quote'),
      shortcut: shortcuts.quote,
      onSelect: applyQuote,
    },
    {
      id: '/ul',
      label: t('reports.review.editor.slashCommands.items.bullet'),
      searchText: t('reports.review.editor.slashCommands.search.listBullet'),
      shortcut: shortcuts.bullets,
      onSelect: () => toggleList('bullet'),
    },
    {
      id: '/ol',
      label: t('reports.review.editor.slashCommands.items.numbered'),
      searchText: t('reports.review.editor.slashCommands.search.listNumber'),
      shortcut: shortcuts.numbered,
      onSelect: () => toggleList('number'),
    },
    {
      id: '/table',
      label: t('reports.review.editor.slashCommands.items.table'),
      searchText: t('reports.review.editor.slashCommands.search.insertTable'),
      shortcut: shortcuts.table,
      onSelect: handleInsertTable,
    },
    {
      id: '/image',
      label: t('reports.review.editor.slashCommands.items.image'),
      searchText: t('reports.review.editor.slashCommands.search.insertImage'),
      shortcut: shortcuts.image,
      onSelect: handleInsertImagePlaceholder,
    },
    {
      id: '/chart',
      label: t('reports.review.editor.slashCommands.items.chart'),
      searchText: t('reports.review.editor.slashCommands.search.insertChart'),
      shortcut: '/chart',
      onSelect: handleInsertChart,
    },
    {
      id: '/map',
      label: t('reports.review.editor.slashCommands.items.map'),
      searchText: t('reports.review.editor.slashCommands.search.insertMap'),
      shortcut: '/map',
      onSelect: handleInsertMap,
    },
  ]), [
    applyHeading,
    applyParagraph,
    applyQuote,
    handleInsertChart,
    handleInsertImagePlaceholder,
    handleInsertMap,
    handleInsertTable,
    shortcuts,
    t,
    toggleList,
  ]);

  const findLabels = useMemo(() => ({
    title: t('reports.review.editor.findReplace.title'),
    close: t('common.close'),
    searchPlaceholder: t('reports.review.editor.findReplace.searchPlaceholder'),
    replacePlaceholder: t('reports.review.editor.findReplace.replacePlaceholder'),
    previous: t('reports.review.editor.findReplace.previous'),
    next: t('reports.review.editor.findReplace.next'),
    replace: t('reports.review.editor.findReplace.replace'),
    replaceAll: t('reports.review.editor.findReplace.replaceAll'),
    caseSensitive: t('reports.review.editor.findReplace.caseSensitive'),
    noMatches: t('reports.review.editor.findReplace.noMatches'),
    replacedCount: t('reports.review.editor.findReplace.replacedCount'),
  }), [t]);

  const currentStyleLabel = labels[getStyleLabelKeyByBlockType(blockType)] || labels.paragraph;

  return (
    <>
      <OptionalPortal target={toolbarPortalTarget}>
        <div
          className={`report-draft-editor__toolbar-wrap${
            toolbarPortalTarget ? ' report-draft-editor__toolbar-wrap--external' : ''
          }`}
          ref={toolbarRef}
        >
        <div className="report-draft-editor__toolbar" ref={toolbarBarRef}>
          <div className="report-draft-editor__toolbar-left" ref={toolbarLeftRef}>
            <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--segmented">
              <button
                type="button"
                className="report-draft-editor__toolbar-button"
                aria-label={labels.undo}
                title={getTooltip(labels.undo, shortcuts.undo)}
                onMouseDown={preventToolbarMouseDown}
                onClick={() => runMutatingToolbarCommand(handleUndo, { restoreSelection: false })}
              >
                <Undo2 size={18} />
              </button>
              <button
                type="button"
                className="report-draft-editor__toolbar-button"
                aria-label={labels.redo}
                title={getTooltip(labels.redo, shortcuts.redo)}
                onMouseDown={preventToolbarMouseDown}
                onClick={() => runMutatingToolbarCommand(handleRedo, { restoreSelection: false })}
              >
                <Redo2 size={18} />
              </button>
            </div>

            <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--field">
              <ReportDraftStyleMenu
                label={labels.styles}
                currentLabel={currentStyleLabel}
                items={styleMenuItems}
                wrapperZIndex={TOOLBAR_POPOVER_Z_INDEX}
              />
            </div>

            <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--segmented">
              {primaryInlineKeys.map(renderActionButton)}
            </div>

            <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--segmented">
              {primaryAlignmentKeys.map(renderActionButton)}
            </div>

            <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--segmented">
              {primaryListKeys.map(renderActionButton)}
            </div>

            <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--field">
              <ReportDraftInsertMenu
                label={labels.insert}
                sections={insertMenuSections}
                wrapperZIndex={TOOLBAR_POPOVER_Z_INDEX}
              />
            </div>

            <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--segmented">
              {advancedEditorEnabled ? (
                <>
                  {isSpaciousToolbar ? (
                    <>
                      <button
                        type="button"
                        className={`report-draft-editor__toolbar-action${isFindReplaceOpen ? ' is-active' : ''}`}
                        aria-label={labels.findReplace}
                        title={getTooltip(labels.findReplace, shortcuts.find)}
                        aria-pressed={isFindReplaceOpen}
                        onMouseDown={preventToolbarMouseDown}
                        onClick={handleToggleFindReplacePanel}
                      >
                        <Search size={16} />
                        <span className="report-draft-editor__toolbar-action-label">{utilityLabels.find}</span>
                      </button>
                      <button
                        type="button"
                        className={`report-draft-editor__toolbar-action${isPaletteOpen ? ' is-active' : ''}`}
                        aria-label={labels.commandPalette}
                        title={getTooltip(labels.commandPalette, shortcuts.command)}
                        aria-pressed={isPaletteOpen}
                        onMouseDown={preventToolbarMouseDown}
                        onClick={handleOpenCommandPalette}
                      >
                        <CommandIcon size={16} />
                        <span className="report-draft-editor__toolbar-action-label">{utilityLabels.command}</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={`report-draft-editor__toolbar-button${isFindReplaceOpen ? ' is-active' : ''}`}
                        aria-label={labels.findReplace}
                        title={getTooltip(labels.findReplace, shortcuts.find)}
                        onMouseDown={preventToolbarMouseDown}
                        onClick={handleToggleFindReplacePanel}
                      >
                        <Search size={18} />
                      </button>
                      <button
                        type="button"
                        className={`report-draft-editor__toolbar-button${isPaletteOpen ? ' is-active' : ''}`}
                        aria-label={labels.commandPalette}
                        title={getTooltip(labels.commandPalette, shortcuts.command)}
                        onMouseDown={preventToolbarMouseDown}
                        onClick={handleOpenCommandPalette}
                      >
                        <CommandIcon size={18} />
                      </button>
                    </>
                  )}
                </>
              ) : null}
            </div>

            {showOverflowMenu ? (
              <div className="report-draft-editor__toolbar-group report-draft-editor__toolbar-group--field report-draft-editor__toolbar-group--menu">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="report-draft-editor__toolbar-dropdown report-draft-editor__toolbar-dropdown--more"
                      aria-label={labels.more}
                      title={labels.more}
                      onMouseDown={preventToolbarMouseDown}
                    >
                      <MoreHorizontal size={16} />
                      <span className="report-draft-editor__toolbar-dropdown-label">{labels.more}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    sideOffset={2}
                    wrapperZIndex={TOOLBAR_POPOVER_Z_INDEX}
                    className="report-draft-editor__toolbar-menu report-draft-editor__toolbar-menu--more"
                  >
                    {overflowMenuSections.map((section) => (
                      <div key={section.id} className="report-draft-editor__toolbar-menu-section">
                        {section.label ? (
                          <div className="report-draft-editor__toolbar-menu-section-label">{section.label}</div>
                        ) : null}
                        {section.items.map((actionKey) => {
                          const action = actionRegistry[actionKey];
                          if (!action) {
                            return null;
                          }
                          const ActionIcon = action.icon;
                          return (
                            <button
                              key={`overflow-${actionKey}`}
                              type="button"
                              className="report-draft-editor__toolbar-menu-item"
                              onMouseDown={preventToolbarMouseDown}
                              onClick={action.onSelect}
                              aria-pressed={Boolean(action.active)}
                            >
                              <span className="report-draft-editor__toolbar-menu-item-leading">
                                <span className="report-draft-editor__toolbar-menu-item-icon" aria-hidden="true">
                                  <ActionIcon size={14} />
                                </span>
                                <span className="report-draft-editor__toolbar-menu-item-copy">
                                  <span className="report-draft-editor__toolbar-menu-item-label">{action.label}</span>
                                  {action.shortcut ? (
                                    <span className="report-draft-editor__toolbar-menu-shortcut">{action.shortcut}</span>
                                  ) : null}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            ) : null}
          </div>

          <div className="report-draft-editor__toolbar-meta">
            <span
              className={`report-draft-editor__status report-draft-editor__status--${status}`}
              title={statusDetails || statusLabel}
            >
              {(status === 'saving' || status === 'saving_local' || status === 'saved_local_syncing_remote') ? (
                <Loader2
                  size={12}
                  className="report-draft-editor__status-icon report-draft-editor__status-icon--spinning"
                  aria-hidden="true"
                />
              ) : null}
              <span>{statusLabel}</span>
            </span>
            {statusDetails ? (
              <span className="report-draft-editor__toolbar-meta-text">{statusDetails}</span>
            ) : null}
            {status === 'error' && typeof onRetrySave === 'function' ? (
              <button
                type="button"
                className="report-draft-editor__toolbar-chip report-draft-editor__toolbar-chip--retry"
                onClick={onRetrySave}
                title={lastErrorMessage || t('reports.review.editor.status.retry')}
              >
                <RotateCcw size={14} />
                <span>{t('reports.review.editor.status.retry')}</span>
              </button>
            ) : null}
            {status === 'conflict' && typeof onUseServerDraft === 'function' ? (
              <button
                type="button"
                className="report-draft-editor__toolbar-chip"
                onClick={onUseServerDraft}
                title={t('reports.review.editor.status.useServerDraft')}
              >
                <span>{t('reports.review.editor.status.useServerDraft')}</span>
              </button>
            ) : null}
            {status === 'conflict' && typeof onOverwriteServerDraft === 'function' ? (
              <button
                type="button"
                className="report-draft-editor__toolbar-chip report-draft-editor__toolbar-chip--retry"
                onClick={onOverwriteServerDraft}
                title={t('reports.review.editor.status.overwriteServerDraft')}
              >
                <span>{t('reports.review.editor.status.overwriteServerDraft')}</span>
              </button>
            ) : null}
          </div>
        </div>

        {advancedEditorEnabled && isFindReplaceOpen ? (
          <ReportDraftFindReplacePanel
            labels={findLabels}
            query={findQuery}
            replaceValue={replaceValue}
            caseSensitive={caseSensitive}
            matchCount={findState.matchCount}
            currentMatch={findState.currentMatch}
            lastReplaceCount={findState.replacedCount}
            onQueryChange={(value) => {
              setFindQuery(value);
              setFindState((prev) => ({ ...prev, replacedCount: 0 }));
            }}
            onReplaceChange={(value) => {
              setReplaceValue(value);
            }}
            onToggleCase={() => {
              setCaseSensitive((current) => !current);
              setFindState((prev) => ({ ...prev, replacedCount: 0 }));
            }}
            onPrev={() => {
              findReplaceRef.current?.previous();
            }}
            onNext={() => {
              findReplaceRef.current?.next();
            }}
            onReplace={() => {
              const replacedCount = findReplaceRef.current?.replaceCurrent(replaceValue) || 0;
              setFindState((prev) => ({ ...prev, replacedCount }));
            }}
            onReplaceAll={() => {
              const replacedCount = findReplaceRef.current?.replaceAll(replaceValue) || 0;
              setFindState((prev) => ({ ...prev, replacedCount }));
            }}
            onClose={() => setFindPanelOpen(false)}
          />
        ) : null}
        </div>
      </OptionalPortal>

      {advancedEditorEnabled ? (
        <ReportDraftCommandPalette
          open={isPaletteOpen}
          onOpenChange={setPaletteOpen}
          sections={commandPaletteSections}
          searchPlaceholder={t('reports.review.editor.commandPalette.searchPlaceholder')}
          emptyLabel={t('reports.review.editor.commandPalette.empty')}
        />
      ) : null}
      <ReportDraftVisualPickerDialog
        open={isVisualPickerOpen}
        onOpenChange={handleVisualPickerOpenChange}
        title={visualPickerTitle}
        description={visualPickerDescription}
        sections={visualPickerSections}
        searchPlaceholder={visualPickerSearchPlaceholder}
        emptyLabel={visualPickerLabels.empty}
        interactionMode={visualPickerMode === 'chart' ? 'select_then_place' : 'instant'}
        onInsertAtCursorItem={handleInsertVisualPickerItemAtCursor}
        insertAtCursorLabel={visualPickerLabels.insertAtCursor}
        placementHint={visualPickerLabels.dragToPlaceHint}
        showPlacementHints={visualPickerMode === 'chart'}
        onDragStartItem={handleVisualPickerDragStartItem}
        onDragEndItem={handleVisualPickerDragEndItem}
        isTemporarilyHidden={visualPickerMode === 'chart' && isVisualPickerTemporarilyHidden}
      />
      <SlashCommandPlugin
        enabled={advancedEditorEnabled}
        items={slashItems}
        labels={{
          menuAriaLabel: t('reports.review.editor.slashCommands.menuAriaLabel'),
          prompt: t('reports.review.editor.slashCommands.prompt'),
          noCommands: t('reports.review.editor.slashCommands.noCommands'),
        }}
      />
      {advancedEditorEnabled ? (
        <FindReplacePlugin
          ref={findReplaceRef}
          active={isFindReplaceOpen}
          query={findQuery}
          caseSensitive={caseSensitive}
          onStateChange={handleFindStateChange}
        />
      ) : null}
    </>
  );
}

function ReportDraftEditor({
  templateDocument,
  initialSerializedState,
  initialDraftPayload,
  storageKey,
  reportId,
  locale,
  onAutosave,
  onRegisterPersistenceControls,
  onStatsChange,
  initialLastSavedAt,
  footerContent,
  paginationMode,
  paginationProfile,
  layoutMode,
  toolbarPortalTarget,
}) {
  const { t } = useTranslation();
  const [saveStatus, setSaveStatus] = useState('saved');
  const [lastSavedAt, setLastSavedAt] = useState(initialLastSavedAt);
  const [lastErrorMessage, setLastErrorMessage] = useState(null);
  const [isSlowSaving, setIsSlowSaving] = useState(false);
  const [retrySaveHandler, setRetrySaveHandler] = useState(null);
  const [conflictActions, setConflictActions] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const rootRef = useRef(null);
  const editorContentRef = useRef(null);
  const visualDragBridgeRef = useRef({
    didDropSucceed: false,
    lastClearReason: null,
  });

  const placeholderLabel = t('reports.review.template.placeholders.caption');
  const pagedHeaderLabel = useMemo(
    () => resolvePagedHeaderLabel(templateDocument, t('reports.review.template.cover.reportTitle')),
    [templateDocument, t]
  );
  const templateVersion = templateDocument?.version || null;
  const migratedSerializedState = useMemo(
    () => migrateLegacyVisualPlaceholders(initialSerializedState, templateDocument),
    [initialSerializedState, templateDocument]
  );

  const initialConfig = useMemo(
    () => ({
      namespace: 'AgriSenseReportDraft',
      theme: reportDraftTheme,
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
      nodes: reportDraftNodes,
      editorState:
        migratedSerializedState ||
        (() => buildEditorStateFromTemplate(templateDocument, placeholderLabel)),
    }),
    [migratedSerializedState, templateDocument, placeholderLabel]
  );

  useEffect(() => {
    setLastSavedAt(initialLastSavedAt || null);
  }, [initialLastSavedAt]);

  useEffect(() => {
    const element = rootRef.current;
    if (!element || typeof window === 'undefined') {
      return undefined;
    }

    let frameId = null;

    const updateCenter = () => {
      const rect = element.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      element.style.setProperty('--ai-island-center', `${center}px`);
    };

    const scheduleUpdate = () => {
      if (frameId) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateCenter();
      });
    };

    updateCenter();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(element);
    }

    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', scheduleUpdate);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof onStatsChange === 'function') {
      onStatsChange({ wordCount, saveStatus, lastSavedAt });
    }
  }, [wordCount, saveStatus, lastSavedAt, onStatsChange]);

  const handleStatusChange = useCallback((status, metadata = null) => {
    if (
      status === 'saved'
      || status === 'saved_local_syncing_remote'
      || status === 'saved_local_offline'
    ) {
      setSaveStatus(status);
      setLastSavedAt(metadata?.savedAt instanceof Date ? metadata.savedAt : (lastSavedAt || new Date()));
      setLastErrorMessage(
        status === 'saved_local_offline' ? t('reports.review.editor.status.offlineRetry') : null
      );
      setIsSlowSaving(Boolean(metadata?.isSlow));
      return;
    }
    if (status === 'captured') {
      setSaveStatus('captured');
      setLastErrorMessage(null);
      setIsSlowSaving(false);
      return;
    }
    if (status === 'conflict') {
      setSaveStatus('conflict');
      setLastErrorMessage(
        metadata?.message || t('reports.review.editor.status.conflict')
      );
      setIsSlowSaving(false);
      return;
    }
    if (status === 'sync_error') {
      setSaveStatus('sync_error');
      setLastErrorMessage(
        metadata?.message || t('reports.review.editor.status.syncError')
      );
      setIsSlowSaving(false);
      return;
    }
    if (status === 'error') {
      setSaveStatus('error');
      const errorCode = typeof metadata?.errorCode === 'string' ? metadata.errorCode : null;
      const nextErrorMessage = errorCode === 'quota_exceeded'
        ? t('reports.review.editor.status.quotaExceeded', {
          defaultValue: 'Browser storage is full. Autosave could not persist this draft locally.',
        })
        : (errorCode === 'save_timeout'
          ? t('reports.review.editor.status.saveTimeout', {
            defaultValue: 'Autosave is taking too long. Please retry.',
          })
          : (errorCode
            ? t('reports.review.editor.status.errorDetails')
            : (metadata?.message || t('reports.review.editor.status.errorDetails'))));
      setLastErrorMessage(
        nextErrorMessage
      );
      setIsSlowSaving(false);
      return;
    }
    if (status === 'saving' || status === 'saving_local') {
      const phase = typeof metadata?.phase === 'string' ? metadata.phase : null;
      setLastErrorMessage(null);
      setSaveStatus(status === 'saving_local' ? 'saving_local' : 'saving');
      setIsSlowSaving((current) => (
        typeof metadata?.isSlow === 'boolean'
          ? metadata.isSlow
          : current
      ));
      if (phase === 'queued') {
        setIsSlowSaving(false);
      }
    }
  }, [lastSavedAt, t]);

  const handleRetryRegistration = useCallback((retryFn) => {
    setRetrySaveHandler(() => (typeof retryFn === 'function' ? retryFn : null));
  }, []);

  const handleConflictActionsRegistration = useCallback((actions) => {
    if (!actions || typeof actions !== 'object') {
      setConflictActions(null);
      return;
    }
    setConflictActions({
      useServerDraft: typeof actions.useServerDraft === 'function' ? actions.useServerDraft : null,
      overwriteServerDraft:
        typeof actions.overwriteServerDraft === 'function' ? actions.overwriteServerDraft : null,
    });
  }, []);

  const handleWordCountChange = useCallback((nextCount) => {
    setWordCount((prevCount) => (prevCount === nextCount ? prevCount : nextCount));
  }, []);

  const handleVisualDropCommit = useCallback(() => {
    if (!visualDragBridgeRef.current) {
      return;
    }
    visualDragBridgeRef.current.didDropSucceed = true;
    visualDragBridgeRef.current.lastClearReason = 'drop';
  }, []);

  const handleVisualDragClear = useCallback((reason) => {
    if (!visualDragBridgeRef.current) {
      return;
    }
    visualDragBridgeRef.current.lastClearReason = reason || 'unknown';
    if (reason !== 'drop' && !visualDragBridgeRef.current.didDropSucceed) {
      visualDragBridgeRef.current.didDropSucceed = false;
    }
  }, []);

  return (
    <div className="report-draft-editor" ref={rootRef}>
      <ReportDraftVisualNodeProvider value={{ templateDocument }}>
        <LexicalComposer initialConfig={initialConfig}>
          <div className="report-draft-editor__frame">
            <ReportDraftToolbar
              status={saveStatus}
              lastSavedAt={lastSavedAt}
              lastErrorMessage={lastErrorMessage}
              isSlowSaving={isSlowSaving}
              onRetrySave={retrySaveHandler}
              onUseServerDraft={conflictActions?.useServerDraft || null}
              onOverwriteServerDraft={conflictActions?.overwriteServerDraft || null}
              locale={locale}
              toolbarPortalTarget={toolbarPortalTarget}
              templateDocument={templateDocument}
              visualDragBridgeRef={visualDragBridgeRef}
            />
            <ReportPagedCanvas
              contentRef={editorContentRef}
              readOnly={false}
              showPageIndicator
              paginationMode={paginationMode}
              paginationProfile={paginationProfile}
              headerLabel={pagedHeaderLabel}
              layoutMode={layoutMode}
            >
              <div className="report-draft-editor__page">
                <RichTextPlugin
                  contentEditable={
                    <div className="report-draft-editor__contenteditable-wrap">
                      <ContentEditable
                        className="report-draft-editor__contenteditable"
                        ref={editorContentRef}
                      />
                    </div>
                  }
                  placeholder={
                    <div className="report-draft-editor__placeholder-text">
                      {t('reports.review.editor.placeholder')}
                    </div>
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
                <ReportDraftVisualDropInsertPlugin
                  enabled
                  onVisualDropCommit={handleVisualDropCommit}
                  onVisualDragClear={handleVisualDragClear}
                />
                <HistoryPlugin />
                <ListPlugin />
                <LinkPlugin />
                <TablePlugin />
                <HeadingAnchorPlugin />
                <HeadingNumberingPlugin />
                <WordCountPlugin onWordCountChange={handleWordCountChange} />
                <AutosavePlugin
                  storageKey={storageKey}
                  reportId={reportId}
                  locale={locale}
                  templateVersion={templateVersion}
                  initialDraftPayload={initialDraftPayload}
                  onAutosave={onAutosave}
                  onStatusChange={handleStatusChange}
                  onRegisterRetry={handleRetryRegistration}
                  onRegisterPersistenceControls={onRegisterPersistenceControls}
                  onRegisterConflictActions={handleConflictActionsRegistration}
                />
              </div>
            </ReportPagedCanvas>
            {footerContent}
          </div>
          <AIIslandPlugin
            isExpanded={isAssistantOpen}
            onExpandedChange={setIsAssistantOpen}
          />
        </LexicalComposer>
      </ReportDraftVisualNodeProvider>
    </div>
  );
}

ReportDraftEditor.propTypes = {
  templateDocument: PropTypes.shape({
    sections: PropTypes.array,
  }),
  initialSerializedState: PropTypes.string,
  initialDraftPayload: PropTypes.object,
  storageKey: PropTypes.string,
  reportId: PropTypes.string,
  locale: PropTypes.string,
  onAutosave: PropTypes.func,
  onRegisterPersistenceControls: PropTypes.func,
  onStatsChange: PropTypes.func,
  initialLastSavedAt: PropTypes.instanceOf(Date),
  footerContent: PropTypes.node,
  paginationMode: PropTypes.oneOf(['strict', 'guides', 'continuous']),
  paginationProfile: PropTypes.oneOf(['screen', 'print']),
  layoutMode: PropTypes.oneOf(['strict', 'guides', 'paged_guides', 'continuous']),
  toolbarPortalTarget: PropTypes.any,
};

ReportDraftEditor.defaultProps = {
  templateDocument: null,
  initialSerializedState: null,
  initialDraftPayload: null,
  storageKey: null,
  reportId: null,
  locale: null,
  onAutosave: null,
  onRegisterPersistenceControls: null,
  onStatsChange: null,
  initialLastSavedAt: null,
  footerContent: null,
  paginationMode: 'strict',
  paginationProfile: 'screen',
  layoutMode: null,
  toolbarPortalTarget: null,
};

export {
  ReportDraftEditor,
  reportDraftTheme,
  reportDraftNodes,
  HeadingAnchorPlugin,
  HeadingNumberingPlugin,
};
